/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  NavLink,
  Redirect,
  Route,
  Switch,
} from "react-router-dom";

import "./admin.css";
import NotFoundPage from "../pages/notFound";

const ADMIN_TOKEN_KEY = "adminToken";
const ADMIN_TOKEN_EXPIRATION_KEY = "adminTokenExpiration";

const ALLOWED_DEVICE_GET_PATHS = [
  "/api/device",
  "/api/conf",
  "/api/outlets",
  "/api/inlets",
  "/api/banks",
  "/api/sensors",
  "/api/events",
  "/api/actions",
  "/api/network/vlans",
  "/api/pduRoutes",
  "/api/network/wan-health",
];

const PRIMARY_CUSTOMER_SEGMENT_OPTIONS = [
  { value: "partner", label: "Partner" },
  { value: "msp", label: "MSP" },
  { value: "end_customer", label: "End Customer" },
  { value: "internal_other", label: "Internal / Other" },
];

const USE_CASE_TAG_OPTIONS = [
  { value: "remote_power_recovery", label: "Remote Power Recovery" },
  { value: "alerting_automation", label: "Alerting / Automation" },
  { value: "api_control", label: "API Control" },
  { value: "asset_tracking", label: "Asset Tracking" },
];

const MCP_SCOPE_OPTIONS = [
  {
    value: "mcp.inventory.read",
    label: "Inventory",
    category: "Read",
    risk: "low",
    description: "List visible devices and read device identity/details.",
  },
  {
    value: "mcp.telemetry.read",
    label: "Telemetry",
    category: "Read",
    risk: "low",
    description: "Read live overview and component telemetry.",
  },
  {
    value: "mcp.automation.read",
    label: "Automation",
    category: "Read",
    risk: "low",
    description: "Read device events and action definitions.",
  },
  {
    value: "mcp.logs.read",
    label: "Event Logs",
    category: "Read",
    risk: "low",
    description: "Read device event log history.",
  },
  {
    value: "mcp.activity.read",
    label: "Activity",
    category: "Read",
    risk: "low",
    description: "Read user and device activity history.",
  },
  {
    value: "mcp.configuration.read",
    label: "Configuration",
    category: "Read",
    risk: "low",
    description: "Read PDU configuration.",
  },
  {
    value: "mcp.firmware.read",
    label: "Firmware",
    category: "Read",
    risk: "low",
    description: "Read firmware status without starting updates.",
  },
  {
    value: "mcp.diagnostics.ping",
    label: "Diagnostic Ping",
    category: "Diagnostics",
    risk: "medium",
    description: "Run constrained ping diagnostics from a PDU.",
  },
  {
    value: "mcp.groups.read",
    label: "Device Groups",
    category: "Read",
    risk: "low",
    description: "List visible device groups and group health metadata.",
  },
  {
    value: "mcp.groups.write",
    label: "Device Group Management",
    category: "Write",
    risk: "high",
    description: "Create, edit, and delete device groups for admin PAT users.",
  },
  {
    value: "mcp.power_trends.read",
    label: "Power Trends",
    category: "Read",
    risk: "low",
    description: "Read inlet and outlet power trend buckets and anomaly metadata.",
  },
  {
    value: "mcp.outlets.write",
    label: "Outlet Control",
    category: "Write",
    risk: "high",
    description: "Switch outlets and edit allowlisted outlet settings.",
  },
  {
    value: "mcp.events.write",
    label: "Event Rules",
    category: "Write",
    risk: "high",
    description: "Create, update, and delete device event rules.",
  },
  {
    value: "mcp.configuration.write",
    label: "Configuration Edits",
    category: "Write",
    risk: "high",
    description: "Edit allowlisted PDU configuration fields only.",
  },
  {
    value: "mcp.serial_console.write",
    label: "Serial Console",
    category: "Serial",
    risk: "critical",
    description: "Open managed raw serial console sessions for admin PAT users.",
  },
];

const MCP_SCOPE_VALUES = MCP_SCOPE_OPTIONS.map((option) => option.value);
const MCP_READ_ONLY_SCOPES = MCP_SCOPE_OPTIONS
  .filter((option) => option.category === "Read")
  .map((option) => option.value);

const MCP_SCOPE_PRESETS = [
  {
    key: "read_only",
    label: "Read-only Pilot",
    description: "Inventory, live telemetry, logs, configuration reads, groups, and power trends.",
    scopes: MCP_READ_ONLY_SCOPES,
    pingEnabled: false,
  },
  {
    key: "diagnostics",
    label: "Diagnostics Pilot",
    description: "Read-only plus constrained diagnostic ping.",
    scopes: [...MCP_READ_ONLY_SCOPES, "mcp.diagnostics.ping"],
    pingEnabled: true,
  },
  {
    key: "power_ops",
    label: "Power Operations",
    description: "Diagnostics plus outlet switching and outlet edits.",
    scopes: [...MCP_READ_ONLY_SCOPES, "mcp.diagnostics.ping", "mcp.outlets.write"],
    pingEnabled: true,
  },
  {
    key: "automation",
    label: "Automation Writes",
    description: "Power operations plus event create, edit, and delete.",
    scopes: [
      ...MCP_READ_ONLY_SCOPES,
      "mcp.diagnostics.ping",
      "mcp.outlets.write",
      "mcp.events.write",
    ],
    pingEnabled: true,
  },
  {
    key: "full_pilot",
    label: "Full Pilot",
    description: "All current MCP scopes including configuration edits and serial console.",
    scopes: MCP_SCOPE_VALUES,
    pingEnabled: true,
  },
];

const ENTERPRISE_THEME_DEFAULTS = {
  primary: "#141c25",
  primaryFont: "#ffffff",
  secondary: "#1d262f",
  secondaryFont: "#ffffff",
  tertiary: "#49535D",
  tertiaryFont: "#ffffff",
  quaternary: "#49535D",
  quaternaryFont: "#ffffff",
  quinary: "#6c757e",
  quinaryFont: "#000000",
  highlight: "#141c25",
  highlightFont: "#ffffff",
  logoUrl: "",
  logoHorizontalUrl: "",
  logoWidth: "",
};

const ENTERPRISE_THEME_COLOR_FIELDS = [
  ["primary", "Primary"],
  ["primaryFont", "Primary Text"],
  ["secondary", "Secondary"],
  ["secondaryFont", "Secondary Text"],
  ["tertiary", "Tertiary"],
  ["tertiaryFont", "Tertiary Text"],
  ["quaternary", "Quaternary"],
  ["quaternaryFont", "Quaternary Text"],
  ["quinary", "Quinary"],
  ["quinaryFont", "Quinary Text"],
  ["highlight", "Highlight"],
  ["highlightFont", "Highlight Text"],
];

const ENTERPRISE_THEME_CONTRAST_PAIRS = [
  ["primary", "primaryFont", "Primary"],
  ["secondary", "secondaryFont", "Secondary"],
  ["tertiary", "tertiaryFont", "Tertiary"],
  ["quaternary", "quaternaryFont", "Quaternary"],
  ["quinary", "quinaryFont", "Quinary"],
  ["highlight", "highlightFont", "Highlight"],
];

const normalizePrimaryCustomerSegmentValue = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return PRIMARY_CUSTOMER_SEGMENT_OPTIONS.some(
    (option) => option.value === normalized,
  )
    ? normalized
    : null;
};

const normalizeUseCaseTagsValue = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .map((entry) => String(entry || "").trim().toLowerCase())
        .filter((entry) =>
          USE_CASE_TAG_OPTIONS.some((option) => option.value === entry),
        ),
    ),
  );
};

const normalizeMcpScopeValue = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .map((entry) => String(entry || "").trim())
        .filter((entry) => MCP_SCOPE_VALUES.includes(entry)),
    ),
  );
};

const mergeMcpScopeOptions = (options = []) => {
  const fallbackByValue = new Map(
    MCP_SCOPE_OPTIONS.map((option) => [option.value, option]),
  );
  const source = Array.isArray(options) && options.length ? options : MCP_SCOPE_OPTIONS;

  return source
    .map((option) => ({
      ...(fallbackByValue.get(option.value) || {}),
      ...option,
    }))
    .filter((option) => MCP_SCOPE_VALUES.includes(option.value));
};

const formatMcpRiskLabel = (risk) => {
  switch (risk) {
    case "low":
      return "Low Risk";
    case "medium":
      return "Medium Risk";
    case "high":
      return "High Risk";
    case "critical":
      return "Critical";
    default:
      return "Review";
  }
};

const mcpRiskTone = (risk) => {
  switch (risk) {
    case "low":
      return "success";
    case "medium":
      return "warning";
    case "high":
    case "critical":
      return "danger";
    default:
      return "default";
  }
};

const REPORT_RANGE_DEFAULT_DAYS = 30;
const DEVICE_PAIR_SEARCH_LIMIT = 10;
const DEVICE_PAIR_POLL_INTERVAL_MS = 2000;
const DEVICE_PAIR_POLL_MAX_ATTEMPTS = 20;

const REPORT_FOOTPRINT_SERIES = [
  {
    key: "estimated_device_footprint_day",
    label: "Estimated Device Footprint",
    color: "#ffce8a",
  },
  {
    key: "estimated_active_devices_day",
    label: "Estimated Active Devices",
    color: "#3ecf8e",
  },
  {
    key: "estimated_inactive_devices_day",
    label: "Estimated Inactive Devices",
    color: "#f06d6d",
  },
];

const REPORT_USAGE_SERIES = [
  {
    key: "outlet_actions_day",
    label: "Outlet Actions",
    color: "#ffce8a",
  },
  {
    key: "controller_reboots_day",
    label: "Controller Reboots",
    color: "#f06d6d",
  },
  {
    key: "alert_events_day",
    label: "Alert Events",
    color: "#f39c12",
  },
  {
    key: "automation_runs_day",
    label: "Automation Runs",
    color: "#3ecf8e",
  },
];

const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY) || "";

const clearAdminSession = () => {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_TOKEN_EXPIRATION_KEY);
};

const adminRequest = async (
  method,
  url,
  data,
  onUnauthorized,
  config = {},
) => {
  try {
    return await axios({
      method,
      url,
      data,
      ...config,
      headers: {
        ...(config.headers || {}),
        Authorization: `Bearer ${getAdminToken()}`,
      },
    });
  } catch (error) {
    if (error?.response?.status === 401) {
      clearAdminSession();
      if (typeof onUnauthorized === "function") {
        onUnauthorized();
      }
    }
    throw error;
  }
};

const formatDateTime = (value) => {
  if (!value) {
    return "n/a";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
};

const formatFileSize = (value) => {
  const size = Number(value);
  if (!Number.isFinite(size) || size < 0) {
    return "n/a";
  }

  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  if (size < 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatFirmwareVersionEntry = (entry = {}) => {
  const version = String(entry.version || "").trim();
  const releaseChannel = String(
    entry.releaseChannel || entry.release_channel || "production",
  )
    .trim()
    .toLowerCase();

  if (!version) {
    return "";
  }

  return releaseChannel && releaseChannel !== "production"
    ? `${version} [${releaseChannel}]`
    : version;
};

const extractMessage = (error) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.code ||
    error?.message ||
    "Request failed."
  );
};

const tryParseJsonString = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed || !["{", "["].includes(trimmed[0])) {
    return null;
  }

  try {
    return JSON.parse(trimmed);
  } catch (_error) {
    return null;
  }
};

const extractPairingOfflineAmbiguity = (error) => {
  const queue = [
    error?.response?.data,
    error?.response?.data?.data,
    error?.response?.data?.message,
    error?.response?.data?.error,
    error?.message,
  ];
  const seen = new Set();

  while (queue.length) {
    const current = queue.shift();
    if (current === null || current === undefined) {
      continue;
    }

    if (typeof current === "string") {
      const trimmed = current.trim();
      if (!trimmed || seen.has(trimmed)) {
        continue;
      }
      seen.add(trimmed);

      const parsed = tryParseJsonString(trimmed);
      if (parsed) {
        queue.push(parsed);
      }

      if (
        trimmed.includes("404103") ||
        /requested device isn'?t online/i.test(trimmed)
      ) {
        return {
          errorCode: "404103",
          message: trimmed,
        };
      }

      continue;
    }

    if (typeof current === "object") {
      const errorCode = String(current.errorCode || "").trim();
      const message = String(current.message || current.Message || "").trim();
      if (
        errorCode === "404103" ||
        /requested device isn'?t online/i.test(message)
      ) {
        return {
          errorCode: errorCode || "404103",
          message,
        };
      }

      queue.push(
        current.Message,
        current.message,
        current.error,
        current.details,
        current.data,
      );
    }
  }

  return null;
};

const parseJsonInput = (value) => {
  if (!value.trim()) {
    return [];
  }
  return JSON.parse(value);
};

const copyTextToClipboard = async (value) => {
  if (!window.navigator?.clipboard?.writeText) {
    throw new Error("Clipboard access is unavailable in this browser.");
  }

  await window.navigator.clipboard.writeText(value);
};

const formatSegmentLabel = (value) => {
  return (
    PRIMARY_CUSTOMER_SEGMENT_OPTIONS.find((option) => option.value === value)
      ?.label || "Unassigned"
  );
};

const formatUseCaseLabel = (value) => {
  return (
    USE_CASE_TAG_OPTIONS.find((option) => option.value === value)?.label ||
    "Unassigned"
  );
};

const toggleSelection = (list = [], value) => {
  return list.includes(value)
    ? list.filter((entry) => entry !== value)
    : [...list, value];
};

const getUserIdentifier = (user) => user?.user_id || user?.id;

const formatBooleanLabel = (value) => (value ? "Yes" : "No");

const formatActiveLabel = (value) => {
  if (value === false || value === "false" || value === "0") {
    return "Inactive";
  }
  if (value === null || value === undefined || value === "") {
    return "n/a";
  }
  return "Active";
};

const buildAdminUserProfileForm = (user = {}) => ({
  full_name: user.full_name || "",
  approved: Boolean(user.approved),
  email_verified: Boolean(user.email_verified),
  active:
    user.active === undefined || user.active === null
      ? true
      : !(user.active === false || user.active === "false" || user.active === "0"),
  active_in_enterprise: Boolean(user.active_in_enterprise),
  has_admin_privileges: Boolean(user.has_admin_privileges),
  password_login_disabled: Boolean(user.password_login_disabled),
});

const normalizeEnterpriseTheme = (theme = {}) => ({
  ...ENTERPRISE_THEME_DEFAULTS,
  ...(theme || {}),
});

const isValidHexColor = (value) =>
  /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(value || "").trim());

const expandHexColor = (value) => {
  const normalized = String(value || "").trim().replace("#", "");
  if (normalized.length === 3) {
    return normalized
      .split("")
      .map((entry) => entry + entry)
      .join("");
  }
  return normalized;
};

const hexToRgb = (value) => {
  if (!isValidHexColor(value)) {
    return null;
  }

  const expanded = expandHexColor(value);
  return {
    r: Number.parseInt(expanded.slice(0, 2), 16),
    g: Number.parseInt(expanded.slice(2, 4), 16),
    b: Number.parseInt(expanded.slice(4, 6), 16),
  };
};

const getRelativeLuminance = ({ r, g, b }) => {
  const values = [r, g, b].map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });

  return values[0] * 0.2126 + values[1] * 0.7152 + values[2] * 0.0722;
};

const getContrastRatio = (background, foreground) => {
  const backgroundRgb = hexToRgb(background);
  const foregroundRgb = hexToRgb(foreground);
  if (!backgroundRgb || !foregroundRgb) {
    return null;
  }

  const backgroundLuminance = getRelativeLuminance(backgroundRgb);
  const foregroundLuminance = getRelativeLuminance(foregroundRgb);
  const light = Math.max(backgroundLuminance, foregroundLuminance);
  const dark = Math.min(backgroundLuminance, foregroundLuminance);

  return (light + 0.05) / (dark + 0.05);
};

const getContrastTone = (ratio) => {
  if (!ratio) {
    return "error";
  }

  return ratio >= 4.5 ? "success" : "warning";
};

const formatContrastRatio = (ratio) =>
  ratio ? `${ratio.toFixed(2)}:1` : "Invalid color";

const formatMetricValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "0";
  }

  return numeric.toLocaleString();
};

const formatPercent = (numerator, denominator) => {
  const total = Number(denominator);
  if (!Number.isFinite(total) || total <= 0) {
    return "0%";
  }

  return `${Math.round((Number(numerator || 0) / total) * 100)}%`;
};

const buildUtcDateString = (date) => {
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");
};

const addUtcDays = (dateString, delta) => {
  const [year, month, day] = String(dateString).split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + delta);
  return buildUtcDateString(date);
};

const buildDefaultReportRange = () => {
  const now = new Date();
  const yesterday = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() - 1,
  ));
  const to = buildUtcDateString(yesterday);
  return {
    from: addUtcDays(to, -(REPORT_RANGE_DEFAULT_DAYS - 1)),
    to,
    primarySegment: "",
    useCaseTag: "",
    enterpriseId: "",
  };
};

const formatShortUtcDate = (value) => {
  if (!value) {
    return "n/a";
  }

  const date = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

const combineReportQuality = (left, right) => {
  if (left === "estimated" || right === "estimated") {
    return "estimated";
  }

  if (left === "exact" || right === "exact") {
    return "exact";
  }

  return "unavailable";
};

const summarizeMetricQuality = (rows = [], key) => {
  return rows.reduce(
    (quality, row) =>
      combineReportQuality(quality, row?.metric_quality?.[key] || "unavailable"),
    "unavailable",
  );
};

const formatQualityLabel = (quality) => {
  if (quality === "exact") {
    return "Exact";
  }

  if (quality === "estimated") {
    return "Estimated";
  }

  return "Unavailable";
};

const extractDownloadFileName = (contentDisposition, fallback) => {
  const match = String(contentDisposition || "").match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
};

const downloadBlob = (blob, fileName) => {
  if (!window.URL?.createObjectURL) {
    return;
  }

  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL?.(objectUrl);
};

const buildInitialDevicePairState = () => ({
  search: "",
  loadingSearch: false,
  searched: false,
  enterpriseOptions: [],
  selectedEnterpriseId: "",
  submitting: false,
  error: "",
  info: "",
});

const buildPairPollingState = ({
  enterpriseId,
  canonicalAdminUserId,
  canonicalAdminEmail,
  mode = "accepted",
}) => ({
  enterpriseId: String(enterpriseId || ""),
  canonicalAdminUserId: String(canonicalAdminUserId || ""),
  canonicalAdminEmail,
  mode,
  remainingAttempts: DEVICE_PAIR_POLL_MAX_ATTEMPTS,
});

const SectionCard = ({ title, subtitle, actions, children }) => (
  <section className="admin-card">
    <div className="admin-card-header">
      <div>
        <h2>{title}</h2>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {actions ? <div className="admin-card-actions">{actions}</div> : null}
    </div>
    <div className="admin-card-body">{children}</div>
  </section>
);

const StatCard = ({ label, value, tone = "default" }) => (
  <div className={`admin-stat-card ${tone}`}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const InlineMessage = ({ tone = "info", children }) =>
  children ? <div className={`admin-inline-message ${tone}`}>{children}</div> : null;

const JsonBlock = ({ value }) => (
  <pre className="admin-json-block">
    {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
  </pre>
);

const LoadingView = ({ label = "Loading…" }) => (
  <div className="admin-empty-state">
    <strong>{label}</strong>
  </div>
);

const EmptyState = ({ title, description }) => (
  <div className="admin-empty-state">
    <strong>{title}</strong>
    {description ? <p>{description}</p> : null}
  </div>
);

const QualityBadge = ({ quality }) => (
  <span className={`admin-quality-badge ${quality || "unavailable"}`}>
    {formatQualityLabel(quality)}
  </span>
);

const TagPill = ({ children, tone = "default" }) => (
  <span className={`admin-tag-pill ${tone}`}>{children}</span>
);

const BusinessTaxonomyFields = ({
  primaryCustomerSegment,
  useCaseTags,
  onPrimaryCustomerSegmentChange,
  onUseCaseTagsChange,
}) => {
  const tags = normalizeUseCaseTagsValue(useCaseTags);
  const segment = normalizePrimaryCustomerSegmentValue(primaryCustomerSegment);

  return (
    <>
      <label>
        <span>Primary Customer Segment</span>
        <select
          value={segment || ""}
          onChange={(event) =>
            onPrimaryCustomerSegmentChange(event.target.value || "")
          }
        >
          <option value="">Unassigned</option>
          {PRIMARY_CUSTOMER_SEGMENT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <fieldset className="admin-fieldset admin-fieldset-full">
        <legend>Use Case Tags</legend>
        <div className="admin-chip-selector">
          {USE_CASE_TAG_OPTIONS.map((option) => (
            <label key={option.value} className="admin-chip-option">
              <input
                type="checkbox"
                checked={tags.includes(option.value)}
                onChange={() =>
                  onUseCaseTagsChange(toggleSelection(tags, option.value))
                }
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
    </>
  );
};

const EnterpriseThemeAssetUpload = ({
  assetKind,
  currentUrl,
  enterpriseId,
  label,
  onUploaded,
  onUnauthorized,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");

  const upload = async () => {
    if (!selectedFile) {
      setMessage("Select an image before uploading.");
      return;
    }

    setUploading(true);
    setMessage("");

    try {
      const payload = new FormData();
      payload.append("file", selectedFile);

      const response = await adminRequest(
        "post",
        `/v1/internal-admin/enterprises/${enterpriseId}/ui-theme/assets/${assetKind}`,
        payload,
        onUnauthorized,
      );

      const uploaded = response.data.data || {};
      onUploaded(uploaded.url || "");
      setSelectedFile(null);
      setMessage("Upload complete. Save changes to keep this asset on the enterprise profile.");
    } catch (error) {
      setMessage(extractMessage(error));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="admin-theme-asset-upload">
      <label>
        <span>{label}</span>
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          onChange={(event) => {
            setSelectedFile(event.target.files?.[0] || null);
            setMessage("");
          }}
        />
      </label>
      <div className="admin-upload-meta">
        <strong>{selectedFile?.name || "No file selected."}</strong>
        {selectedFile ? <span>{formatFileSize(selectedFile.size)}</span> : null}
      </div>
      {currentUrl ? (
        <div className="admin-theme-current-asset">
          <span>Current URL</span>
          <strong>{currentUrl}</strong>
        </div>
      ) : null}
      <div className="admin-form-actions">
        <button
          type="button"
          className="admin-secondary-button"
          disabled={!selectedFile || uploading}
          onClick={upload}
        >
          {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>
      <InlineMessage tone={message.includes("complete") ? "success" : "error"}>
        {message}
      </InlineMessage>
    </div>
  );
};

const EnterpriseThemePreview = ({ theme }) => {
  const resolvedTheme = normalizeEnterpriseTheme(theme);
  const logoUrl = resolvedTheme.logoUrl || resolvedTheme.logoHorizontalUrl;

  return (
    <div className="admin-theme-preview">
      <div
        className="admin-theme-preview__sidebar"
        style={{
          background: resolvedTheme.primary,
          color: resolvedTheme.primaryFont,
        }}
      >
        <div className="admin-theme-preview__brand">
          {logoUrl ? (
            <img
              alt=""
              src={logoUrl}
              style={{ maxWidth: resolvedTheme.logoWidth || "140px" }}
            />
          ) : (
            <strong>{resolvedTheme.primary}</strong>
          )}
        </div>
        <div
          className="admin-theme-preview__nav-item active"
          style={{
            background: resolvedTheme.primaryFont,
            color: resolvedTheme.primary,
          }}
        >
          Active Nav
        </div>
        <div className="admin-theme-preview__nav-item">Secondary Nav</div>
      </div>
      <div className="admin-theme-preview__content">
        <div
          className="admin-theme-preview__header"
          style={{
            background: `linear-gradient(180deg, ${resolvedTheme.secondary}, ${resolvedTheme.primary})`,
            color: resolvedTheme.secondaryFont,
          }}
        >
          Header Surface
        </div>
        <div className="admin-theme-preview__surface">
          <button
            type="button"
            style={{
              background: resolvedTheme.highlight,
              color: resolvedTheme.highlightFont,
            }}
          >
            Primary Action
          </button>
          <a
            href="#preview-link"
            onClick={(event) => event.preventDefault()}
            style={{ color: resolvedTheme.highlight }}
          >
            Inline link
          </a>
          <div
            className="admin-theme-preview__chip"
            style={{
              background: resolvedTheme.tertiary,
              color: resolvedTheme.tertiaryFont,
            }}
          >
            Selected Filter
          </div>
          <div className="admin-theme-preview__card">
            <strong>Content Card</strong>
            <span>Content surfaces remain product neutral in dark mode.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const EnterpriseThemeChecks = ({ theme }) => {
  const resolvedTheme = normalizeEnterpriseTheme(theme);

  return (
    <div className="admin-theme-checks">
      {ENTERPRISE_THEME_CONTRAST_PAIRS.map(([backgroundKey, foregroundKey, label]) => {
        const ratio = getContrastRatio(
          resolvedTheme[backgroundKey],
          resolvedTheme[foregroundKey],
        );
        const tone = getContrastTone(ratio);

        return (
          <div key={label} className={`admin-theme-check ${tone}`}>
            <span>{label}</span>
            <strong>{formatContrastRatio(ratio)}</strong>
            <em>{ratio >= 4.5 ? "Passes normal text" : "Needs review"}</em>
          </div>
        );
      })}
    </div>
  );
};

const EnterpriseStylingPanel = ({
  enterprise,
  onThemeChange,
  onUnauthorized,
}) => {
  const theme = normalizeEnterpriseTheme(enterprise.ui_theme);

  return (
    <div className="admin-theme-layout">
      <SectionCard
        title="Enterprise Styling"
        subtitle="These brand colors are treated as light-mode palette values. Dark mode keeps product-owned neutral surfaces."
      >
        <div className="admin-theme-color-grid">
          {ENTERPRISE_THEME_COLOR_FIELDS.map(([field, label]) => (
            <label key={field} className="admin-theme-color-field">
              <span>{label}</span>
              <div className="admin-theme-color-control">
                <input
                  type="color"
                  value={isValidHexColor(theme[field]) ? theme[field] : "#000000"}
                  onChange={(event) => onThemeChange(field, event.target.value)}
                />
                <input
                  type="text"
                  value={theme[field] || ""}
                  onChange={(event) => onThemeChange(field, event.target.value)}
                  placeholder={ENTERPRISE_THEME_DEFAULTS[field]}
                />
              </div>
            </label>
          ))}
        </div>
        <div className="admin-form-grid">
          <label>
            <span>Logo Width</span>
            <input
              type="text"
              value={theme.logoWidth || ""}
              onChange={(event) => onThemeChange("logoWidth", event.target.value)}
              placeholder="170px"
            />
          </label>
          <label>
            <span>Logo URL</span>
            <input
              type="text"
              value={theme.logoUrl || ""}
              onChange={(event) => onThemeChange("logoUrl", event.target.value)}
              placeholder="Upload a logo or paste a public URL"
            />
          </label>
          <label>
            <span>Horizontal Logo URL</span>
            <input
              type="text"
              value={theme.logoHorizontalUrl || ""}
              onChange={(event) =>
                onThemeChange("logoHorizontalUrl", event.target.value)
              }
              placeholder="Upload a horizontal logo or paste a public URL"
            />
          </label>
        </div>
      </SectionCard>

      <SectionCard title="Logo Assets">
        <div className="admin-two-column">
          <EnterpriseThemeAssetUpload
            assetKind="logo"
            currentUrl={theme.logoUrl}
            enterpriseId={enterprise.id}
            label="Sidebar Logo"
            onUnauthorized={onUnauthorized}
            onUploaded={(url) => onThemeChange("logoUrl", url)}
          />
          <EnterpriseThemeAssetUpload
            assetKind="logoHorizontal"
            currentUrl={theme.logoHorizontalUrl}
            enterpriseId={enterprise.id}
            label="Horizontal Logo"
            onUnauthorized={onUnauthorized}
            onUploaded={(url) => onThemeChange("logoHorizontalUrl", url)}
          />
        </div>
      </SectionCard>

      <div className="admin-two-column">
        <SectionCard title="Preview">
          <EnterpriseThemePreview theme={theme} />
        </SectionCard>
        <SectionCard
          title="Contrast Checks"
          subtitle="Normal text should be at least 4.5:1."
        >
          <EnterpriseThemeChecks theme={theme} />
        </SectionCard>
      </div>
    </div>
  );
};

const MultiSeriesTrendChart = ({
  title,
  subtitle,
  rows,
  series,
}) => {
  const width = 760;
  const height = 260;
  const padding = {
    top: 20,
    right: 24,
    bottom: 34,
    left: 24,
  };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const hasData = rows.some((row) =>
    series.some((entry) => (row?.metric_quality?.[entry.key] || "unavailable") !== "unavailable"),
  );
  const allValues = rows.flatMap((row) =>
    series.map((entry) => Number(row?.metrics?.[entry.key] || 0)),
  );
  const maxValue = Math.max(...allValues, 1);
  const xStep = rows.length > 1 ? chartWidth / (rows.length - 1) : 0;

  const buildPoint = (value, index) => {
    const x = padding.left + (rows.length > 1 ? xStep * index : chartWidth / 2);
    const normalized = Math.max(0, Number(value || 0)) / maxValue;
    const y = padding.top + (chartHeight - normalized * chartHeight);
    return { x, y };
  };

  const buildPath = (metricKey) => {
    return rows
      .map((row, index) => {
        const point = buildPoint(row?.metrics?.[metricKey], index);
        return `${index === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
      })
      .join(" ");
  };

  return (
    <SectionCard title={title} subtitle={subtitle}>
      {!hasData ? (
        <EmptyState
          title="No snapshot data available yet."
          description="Run the backfill or wait for the next daily snapshot to populate this chart."
        />
      ) : (
        <>
          <div className="admin-chart-legend">
            {series.map((entry) => (
              <div key={entry.key} className="admin-chart-legend-item">
                <span
                  className="admin-chart-swatch"
                  style={{ background: entry.color }}
                />
                <span>{entry.label}</span>
                <QualityBadge quality={summarizeMetricQuality(rows, entry.key)} />
              </div>
            ))}
          </div>

          <div className="admin-chart-shell">
            <svg
              className="admin-chart-svg"
              viewBox={`0 0 ${width} ${height}`}
              role="img"
              aria-label={title}
            >
              {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
                const y = padding.top + chartHeight - (chartHeight * fraction);
                return (
                  <line
                    key={fraction}
                    x1={padding.left}
                    y1={y}
                    x2={width - padding.right}
                    y2={y}
                    className="admin-chart-grid-line"
                  />
                );
              })}
              {series.map((entry) => (
                <path
                  key={entry.key}
                  d={buildPath(entry.key)}
                  fill="none"
                  stroke={entry.color}
                  strokeWidth="3"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              ))}
              {series.map((entry) =>
                rows.map((row, index) => {
                  const point = buildPoint(row?.metrics?.[entry.key], index);
                  return (
                    <circle
                      key={`${entry.key}-${row.date}`}
                      cx={point.x}
                      cy={point.y}
                      r="3.5"
                      fill={entry.color}
                    />
                  );
                }),
              )}
            </svg>
            <div className="admin-chart-axis">
              <span>{rows[0] ? formatShortUtcDate(rows[0].date) : "n/a"}</span>
              <span>Max {formatMetricValue(maxValue)}</span>
              <span>
                {rows.length ? formatShortUtcDate(rows[rows.length - 1].date) : "n/a"}
              </span>
            </div>
          </div>
        </>
      )}
    </SectionCard>
  );
};

const AdminLoginPage = ({ enabled, loading, errorMessage, onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [stayLoggedIn, setStayLoggedIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await onLogin({ email, password, stayLoggedIn });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-login-screen">
      <div className="admin-login-panel">
        <div className="admin-login-intro">
          <div className="admin-login-badge">Restricted Area</div>
          <h1>Sign in required</h1>
          <p>This page is available only to authorized staff.</p>
        </div>

        {loading ? <LoadingView label="Checking access…" /> : null}
        {!enabled && !loading ? (
          <EmptyState
            title="This page is unavailable."
            description="If you believe you should have access, contact your administrator."
          />
        ) : null}

        {enabled ? (
          <form className="admin-form" onSubmit={handleSubmit}>
            <InlineMessage tone="error">{errorMessage}</InlineMessage>
            <label>
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                required
              />
            </label>
            <label>
              <span>Password</span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <label className="admin-checkbox">
              <input
                type="checkbox"
                checked={stayLoggedIn}
                onChange={(event) => setStayLoggedIn(event.target.checked)}
              />
              <span>Keep this admin session active longer</span>
            </label>
            <button type="submit" className="admin-primary-button" disabled={submitting}>
              {submitting ? "Signing in…" : "Sign In"}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
};

const AdminLayout = ({ adminUser, onLogout, children }) => {
  const navItems = [
    { to: "/admin", label: "Overview", exact: true },
    { to: "/admin/enterprises", label: "Enterprises" },
    { to: "/admin/users", label: "Users" },
    { to: "/admin/reports/business", label: "Reports" },
    { to: "/admin/firmware-catalog", label: "Firmware Catalog" },
    { to: "/admin/audit", label: "Audit" },
  ];

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-brand">
          <span>Synaccess</span>
          <strong>Internal Admin</strong>
        </div>
        <nav className="admin-sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              exact={item.exact}
              className="admin-sidebar-link"
              activeClassName="active"
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="admin-sidebar-footer">
          <div className="admin-sidebar-user">
            <span>Signed in</span>
            <strong>{adminUser?.email_id}</strong>
          </div>
          <button type="button" className="admin-secondary-button" onClick={onLogout}>
            Sign Out
          </button>
        </div>
      </aside>

      <main className="admin-content">{children}</main>
    </div>
  );
};

const DashboardPage = ({ history, onUnauthorized }) => {
  const [state, setState] = useState({
    loading: true,
    error: "",
    data: null,
  });
  const [search, setSearch] = useState("");

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const response = await adminRequest(
          "get",
          "/v1/internal-admin/dashboard",
          null,
          onUnauthorized,
        );
        if (!active) {
          return;
        }
        setState({
          loading: false,
          error: "",
          data: response.data.data,
        });
      } catch (error) {
        if (!active) {
          return;
        }
        setState({
          loading: false,
          error: extractMessage(error),
          data: null,
        });
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [onUnauthorized]);

  const handleSearch = (event) => {
    event.preventDefault();
    const normalized = search.trim();
    if (!normalized) {
      return;
    }

    if (/^\d+$/.test(normalized)) {
      history.push(`/admin/devices/${normalized}`);
      return;
    }

    history.push(`/admin/enterprises?search=${encodeURIComponent(normalized)}`);
  };

  if (state.loading) {
    return <LoadingView />;
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Overview</h1>
          <p>Deployment-wide support and control surface.</p>
        </div>
      </div>

      <SectionCard title="Global Lookup" subtitle="Jump by serial number or enterprise search.">
        <form className="admin-search-form" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Serial number, enterprise name, enterprise id, customer email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <button type="submit" className="admin-primary-button">
            Search
          </button>
        </form>
        <InlineMessage tone="error">{state.error}</InlineMessage>
      </SectionCard>

      <div className="admin-stats-grid">
        <StatCard label="Enterprises" value={state.data?.enterpriseCount ?? 0} />
        <StatCard label="Devices" value={state.data?.deviceCount ?? 0} />
        <StatCard
          label="Online Devices"
          value={state.data?.onlineDeviceCount ?? 0}
          tone="success"
        />
        <StatCard
          label="Offline Devices"
          value={state.data?.offlineDeviceCount ?? 0}
          tone="warning"
        />
      </div>

      <SectionCard title="Recent Audit">
        {state.data?.recentAudit?.length ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Target</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {state.data.recentAudit.map((row) => (
                <tr key={row.id}>
                  <td>{formatDateTime(row.created_at)}</td>
                  <td>{row.action}</td>
                  <td>
                    {row.target_type}:{row.target_id || "n/a"}
                  </td>
                  <td>{row.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState title="No audit rows yet." />
        )}
      </SectionCard>
    </div>
  );
};

export const EnterprisesListPage = ({ history, location, onUnauthorized }) => {
  const parseArchivedFilter = (searchValue) => {
    const rawValue = new URLSearchParams(searchValue).get("archived");
    return rawValue === "true" || rawValue === "all" ? rawValue : "false";
  };
  const [search, setSearch] = useState(
    new URLSearchParams(location.search).get("search") || "",
  );
  const [archivedFilter, setArchivedFilter] = useState(
    parseArchivedFilter(location.search),
  );
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const currentSearch = new URLSearchParams(location.search).get("search") || "";
    const currentArchivedFilter = parseArchivedFilter(location.search);
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const params = {
          search: currentSearch,
        };
        if (currentArchivedFilter !== "all") {
          params.archived = currentArchivedFilter;
        }
        const response = await adminRequest(
          "get",
          "/v1/internal-admin/enterprises",
          null,
          onUnauthorized,
          {
            params,
          },
        );
        if (!active) {
          return;
        }
        setRows(response.data.data || []);
      } catch (error) {
        if (!active) {
          return;
        }
        setError(extractMessage(error));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [location.search, onUnauthorized]);

  useEffect(() => {
    setSearch(new URLSearchParams(location.search).get("search") || "");
    setArchivedFilter(parseArchivedFilter(location.search));
  }, [location.search]);

  const handleSearch = (event) => {
    event.preventDefault();
    const nextSearch = search.trim();
    const nextParams = new URLSearchParams();
    if (nextSearch) {
      nextParams.set("search", nextSearch);
    }
    if (archivedFilter === "all") {
      nextParams.set("archived", "all");
    } else {
      nextParams.set("archived", archivedFilter);
    }
    const nextQuery = nextParams.toString();
    history.push(`/admin/enterprises${nextQuery ? `?${nextQuery}` : ""}`);
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Enterprises</h1>
          <p>Search, inspect, and manage tenant-wide settings.</p>
        </div>
        <button
          type="button"
          className="admin-primary-button"
          onClick={() => history.push("/admin/enterprises/new")}
        >
          New Enterprise
        </button>
      </div>

      <SectionCard title="Filters">
        <form className="admin-enterprise-filter-form" onSubmit={handleSearch}>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name, id, admin email, enterprise uuid"
          />
          <div className="admin-enterprise-filter-controls">
            <label className="admin-enterprise-filter-field">
              <span>Archive Status</span>
              <select
                value={archivedFilter}
                onChange={(event) => setArchivedFilter(event.target.value)}
              >
                <option value="false">Active</option>
                <option value="true">Archived</option>
                <option value="all">All</option>
              </select>
            </label>
            <button type="submit" className="admin-primary-button">
              Apply
            </button>
          </div>
        </form>
        <InlineMessage tone="error">{error}</InlineMessage>
      </SectionCard>

      <SectionCard title="Enterprise List">
        {loading ? <LoadingView /> : null}
        {!loading && !rows.length ? (
          <EmptyState title="No enterprises matched this filter." />
        ) : null}
        {!loading && rows.length ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>ID</th>
                <th>Admin</th>
                <th>Segment</th>
                <th>Use Cases</th>
                <th>Users</th>
                <th>Devices</th>
                <th>SSO</th>
                <th>Archived</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="clickable"
                  onClick={() => history.push(`/admin/enterprises/${row.id}`)}
                >
                  <td>{row.name}</td>
                  <td>{row.id}</td>
                  <td>{row.canonicalAdminUser?.email_id || "n/a"}</td>
                  <td>{formatSegmentLabel(row.primary_customer_segment)}</td>
                  <td>
                    {row.use_case_tags?.length ? (
                      <div className="admin-tag-list">
                        {row.use_case_tags.map((tag) => (
                          <TagPill key={`${row.id}-${tag}`}>
                            {formatUseCaseLabel(tag)}
                          </TagPill>
                        ))}
                      </div>
                    ) : (
                      "Unassigned"
                    )}
                  </td>
                  <td>{row.userCount}</td>
                  <td>{row.deviceCount}</td>
                  <td>
                    {[
                      row.SAML_ENABLED ? "SAML" : null,
                      row.OIDC_ENABLED
                        ? `OIDC${row.OIDC_PROVIDER_TYPE ? ` (${row.OIDC_PROVIDER_TYPE})` : ""}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(", ") || "Disabled"}
                  </td>
                  <td>{row.archived ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </SectionCard>
    </div>
  );
};

export const EnterpriseCreatePage = ({ history, onUnauthorized }) => {
  const [form, setForm] = useState({
    name: "",
    bootstrapAdminEmail: "",
    bootstrapAdminFullName: "",
    max_num_seats: 1000,
    email_verification_required: false,
    auto_approval_on: true,
    SAML_ENABLED: false,
    OIDC_ENABLED: false,
    require_sso_for_sign_in: false,
    max_devices_connected: 10000,
    custom_logging_enabled: false,
    custom_logging_depth_in_days: 90,
    custom_logging_poll_rate_enabled: false,
    custom_logging_poll_rate_in_seconds: 3600,
    cost_per_device_in_cents: 200,
    primary_customer_segment: "",
    use_case_tags: [],
    sendInvite: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const response = await adminRequest(
        "post",
        "/v1/internal-admin/enterprises",
        form,
        onUnauthorized,
      );
      history.push(`/admin/enterprises/${response.data.data.id}`);
    } catch (error) {
      setError(extractMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>New Enterprise</h1>
          <p>Create an enterprise and bootstrap the canonical enterprise admin.</p>
        </div>
      </div>

      <SectionCard title="Enterprise Provisioning">
        <form className="admin-form admin-form-grid" onSubmit={handleSubmit}>
          <InlineMessage tone="error">{error}</InlineMessage>
          <label>
            <span>Enterprise Name</span>
            <input
              type="text"
              value={form.name}
              onChange={(event) => handleChange("name", event.target.value)}
              required
            />
          </label>
          <label>
            <span>Bootstrap Admin Email</span>
            <input
              type="email"
              value={form.bootstrapAdminEmail}
              onChange={(event) =>
                handleChange("bootstrapAdminEmail", event.target.value)
              }
              required
            />
          </label>
          <label>
            <span>Bootstrap Admin Name</span>
            <input
              type="text"
              value={form.bootstrapAdminFullName}
              onChange={(event) =>
                handleChange("bootstrapAdminFullName", event.target.value)
              }
            />
          </label>
          <label>
            <span>Seat Cap</span>
            <input
              type="number"
              value={form.max_num_seats}
              onChange={(event) =>
                handleChange("max_num_seats", Number(event.target.value))
              }
            />
          </label>
          <label>
            <span>Max Devices Connected</span>
            <input
              type="number"
              value={form.max_devices_connected}
              onChange={(event) =>
                handleChange("max_devices_connected", Number(event.target.value))
              }
            />
          </label>
          <label>
            <span>Custom Logging Depth (days)</span>
            <input
              type="number"
              value={form.custom_logging_depth_in_days}
              onChange={(event) =>
                handleChange(
                  "custom_logging_depth_in_days",
                  Number(event.target.value),
                )
              }
            />
          </label>
          <label>
            <span>Custom Logging Poll Rate (seconds)</span>
            <input
              type="number"
              value={form.custom_logging_poll_rate_in_seconds}
              onChange={(event) =>
                handleChange(
                  "custom_logging_poll_rate_in_seconds",
                  Number(event.target.value),
                )
              }
            />
          </label>
          <label>
            <span>Cost Per Device (cents)</span>
            <input
              type="number"
              value={form.cost_per_device_in_cents}
              onChange={(event) =>
                handleChange(
                  "cost_per_device_in_cents",
                  Number(event.target.value),
                )
              }
            />
          </label>
          <BusinessTaxonomyFields
            primaryCustomerSegment={form.primary_customer_segment}
            useCaseTags={form.use_case_tags}
            onPrimaryCustomerSegmentChange={(value) =>
              handleChange("primary_customer_segment", value || null)
            }
            onUseCaseTagsChange={(value) =>
              handleChange("use_case_tags", value)
            }
          />
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={form.email_verification_required}
              onChange={(event) =>
                handleChange("email_verification_required", event.target.checked)
              }
            />
            <span>Email verification required</span>
          </label>
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={form.auto_approval_on}
              onChange={(event) =>
                handleChange("auto_approval_on", event.target.checked)
              }
            />
            <span>Auto approve members</span>
          </label>
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={form.SAML_ENABLED}
              onChange={(event) =>
                handleChange("SAML_ENABLED", event.target.checked)
              }
            />
            <span>Enable SAML</span>
          </label>
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={form.OIDC_ENABLED}
              onChange={(event) =>
                handleChange("OIDC_ENABLED", event.target.checked)
              }
            />
            <span>Enable OIDC</span>
          </label>
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={form.require_sso_for_sign_in}
              onChange={(event) =>
                handleChange("require_sso_for_sign_in", event.target.checked)
              }
            />
            <span>Require SSO for sign-in</span>
          </label>
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={form.custom_logging_enabled}
              onChange={(event) =>
                handleChange("custom_logging_enabled", event.target.checked)
              }
            />
            <span>Enable custom logging</span>
          </label>
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={form.custom_logging_poll_rate_enabled}
              onChange={(event) =>
                handleChange(
                  "custom_logging_poll_rate_enabled",
                  event.target.checked,
                )
              }
            />
            <span>Use custom logging poll rate</span>
          </label>
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={form.sendInvite}
              onChange={(event) =>
                handleChange("sendInvite", event.target.checked)
              }
            />
            <span>Send enterprise admin invite immediately</span>
          </label>
          <div className="admin-form-actions">
            <button type="submit" className="admin-primary-button" disabled={submitting}>
              {submitting ? "Creating…" : "Create Enterprise"}
            </button>
          </div>
        </form>
      </SectionCard>
    </div>
  );
};

export const AdminUsersListPage = ({ history, location, onUnauthorized }) => {
  const locationSearch = location?.search || "";
  const currentParams = new URLSearchParams(locationSearch);
  const [search, setSearch] = useState(currentParams.get("search") || "");
  const [enterpriseFilter, setEnterpriseFilter] = useState(
    currentParams.get("enterpriseId") || "all",
  );
  const [approvedFilter, setApprovedFilter] = useState(
    currentParams.get("approved") || "all",
  );
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const paramsFromLocation = new URLSearchParams(location?.search || "");
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const params = {
          search: paramsFromLocation.get("search") || "",
          limit: 100,
        };
        const nextEnterpriseFilter =
          paramsFromLocation.get("enterpriseId") || "all";
        const nextApprovedFilter = paramsFromLocation.get("approved") || "all";
        if (nextEnterpriseFilter !== "all") {
          params.enterpriseId = nextEnterpriseFilter;
        }
        if (nextApprovedFilter !== "all") {
          params.approved = nextApprovedFilter;
        }
        const response = await adminRequest(
          "get",
          "/v1/internal-admin/users",
          null,
          onUnauthorized,
          {
            params,
          },
        );
        if (!active) {
          return;
        }
        setRows(response.data.data || []);
        setMeta(response.data.meta || null);
      } catch (error) {
        if (active) {
          setError(extractMessage(error));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [location?.search, onUnauthorized]);

  useEffect(() => {
    const params = new URLSearchParams(location?.search || "");
    setSearch(params.get("search") || "");
    setEnterpriseFilter(params.get("enterpriseId") || "all");
    setApprovedFilter(params.get("approved") || "all");
  }, [location?.search]);

  const handleSearch = (event) => {
    event.preventDefault();
    const nextParams = new URLSearchParams();
    const nextSearch = search.trim();
    if (nextSearch) {
      nextParams.set("search", nextSearch);
    }
    if (enterpriseFilter !== "all") {
      nextParams.set("enterpriseId", enterpriseFilter);
    }
    if (approvedFilter !== "all") {
      nextParams.set("approved", approvedFilter);
    }
    const nextQuery = nextParams.toString();
    history.push(`/admin/users${nextQuery ? `?${nextQuery}` : ""}`);
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Users</h1>
          <p>Search cloud accounts and review enterprise assignment.</p>
        </div>
      </div>

      <SectionCard title="Filters">
        <form className="admin-enterprise-filter-form" onSubmit={handleSearch}>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by email, name, company, user id, enterprise"
          />
          <div className="admin-enterprise-filter-controls">
            <label className="admin-enterprise-filter-field">
              <span>Enterprise</span>
              <select
                value={enterpriseFilter}
                onChange={(event) => setEnterpriseFilter(event.target.value)}
              >
                <option value="all">All</option>
                <option value="unassigned">Unassigned</option>
              </select>
            </label>
            <label className="admin-enterprise-filter-field">
              <span>Approval</span>
              <select
                value={approvedFilter}
                onChange={(event) => setApprovedFilter(event.target.value)}
              >
                <option value="all">All</option>
                <option value="false">Not Approved</option>
                <option value="true">Approved</option>
              </select>
            </label>
            <button type="submit" className="admin-primary-button">
              Apply
            </button>
          </div>
        </form>
        <InlineMessage tone="error">{error}</InlineMessage>
      </SectionCard>

      <SectionCard
        title="User List"
        subtitle={
          meta ? `${meta.totalCount || rows.length} total users matched` : ""
        }
      >
        {loading ? <LoadingView /> : null}
        {!loading && !rows.length ? (
          <EmptyState title="No users matched this filter." />
        ) : null}
        {!loading && rows.length ? (
          <div className="admin-table-shell">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Enterprise</th>
                  <th>Approved</th>
                  <th>Verified</th>
                  <th>Active</th>
                  <th>Admin</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((user) => (
                  <tr
                    key={getUserIdentifier(user)}
                    className="clickable"
                    onClick={() =>
                      history.push(`/admin/users/${getUserIdentifier(user)}`)
                    }
                  >
                    <td>
                      <div className="admin-user-identity-cell">
                        <strong>{user.email_id}</strong>
                        <span>{user.user_id || user.id}</span>
                      </div>
                    </td>
                    <td>{user.full_name || "No name set"}</td>
                    <td>{user.company || "n/a"}</td>
                    <td>{user.enterprise?.name || "Unassigned"}</td>
                    <td>{formatBooleanLabel(user.approved)}</td>
                    <td>{formatBooleanLabel(user.email_verified)}</td>
                    <td>{formatActiveLabel(user.active)}</td>
                    <td>
                      {user.is_enterprise_admin || user.has_admin_privileges
                        ? "Yes"
                        : "No"}
                    </td>
                    <td>{formatDateTime(user.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </SectionCard>
    </div>
  );
};

export const AdminUserProfilePage = ({ match, history, onUnauthorized }) => {
  const userId = match.params.userId;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [user, setUser] = useState(null);
  const [enterpriseOptions, setEnterpriseOptions] = useState([]);
  const [profileForm, setProfileForm] = useState(buildAdminUserProfileForm());
  const [assignmentEnterpriseId, setAssignmentEnterpriseId] = useState("");

  const applyUserState = (nextUser) => {
    setUser(nextUser);
    setProfileForm(buildAdminUserProfileForm(nextUser));
    setAssignmentEnterpriseId(String(nextUser?.enterprise_Id || ""));
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [userResponse, enterpriseResponse] = await Promise.all([
          adminRequest(
            "get",
            `/v1/internal-admin/users/${userId}`,
            null,
            onUnauthorized,
          ),
          adminRequest(
            "get",
            "/v1/internal-admin/enterprises",
            null,
            onUnauthorized,
            {
              params: {
                archived: "false",
                limit: 100,
              },
            },
          ),
        ]);
        if (!active) {
          return;
        }
        applyUserState(userResponse.data.data);
        setEnterpriseOptions(enterpriseResponse.data.data || []);
      } catch (error) {
        if (active) {
          setError(extractMessage(error));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [userId, onUnauthorized]);

  const updateProfileForm = (key, value) => {
    setProfileForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const response = await adminRequest(
        "put",
        `/v1/internal-admin/users/${getUserIdentifier(user)}`,
        profileForm,
        onUnauthorized,
      );
      applyUserState({
        ...(response.data.data || {}),
        audit: response.data.data?.audit || user.audit || [],
      });
      setMessage("User profile saved.");
    } catch (error) {
      setError(extractMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const saveAssignment = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const response = await adminRequest(
        "put",
        `/v1/internal-admin/users/${getUserIdentifier(user)}`,
        {
          enterprise_Id: assignmentEnterpriseId || null,
          approved: false,
          active_in_enterprise: true,
        },
        onUnauthorized,
      );
      applyUserState({
        ...(response.data.data || {}),
        audit: response.data.data?.audit || user.audit || [],
      });
      setMessage("Enterprise assignment saved.");
    } catch (error) {
      setError(extractMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingView />;
  }

  if (!user) {
    return <EmptyState title="User not found." />;
  }

  const currentEnterpriseId = String(user.enterprise_Id || "");
  const assignmentChanged = assignmentEnterpriseId !== currentEnterpriseId;
  const enterpriseOptionById = new Map(
    enterpriseOptions.map((enterprise) => [String(enterprise.id), enterprise]),
  );
  if (user.enterprise?.id && !enterpriseOptionById.has(String(user.enterprise.id))) {
    enterpriseOptionById.set(String(user.enterprise.id), user.enterprise);
  }
  const enterpriseSelectOptions = Array.from(enterpriseOptionById.values());
  const auditRows = user.audit || [];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>{user.email_id}</h1>
          <p>
            User #{user.id} • {user.enterprise?.name || "Unassigned"}
          </p>
        </div>
        <button
          type="button"
          className="admin-secondary-button"
          onClick={() => history.push("/admin/users")}
        >
          Back to Users
        </button>
      </div>

      <InlineMessage tone="error">{error}</InlineMessage>
      <InlineMessage tone="success">{message}</InlineMessage>

      <div className="admin-stats-grid">
        <StatCard label="Approved" value={formatBooleanLabel(user.approved)} />
        <StatCard
          label="Email Verified"
          value={formatBooleanLabel(user.email_verified)}
        />
        <StatCard label="Active" value={formatActiveLabel(user.active)} />
        <StatCard
          label="Last Login"
          value={formatDateTime(user.last_login)}
        />
      </div>

      <SectionCard title="Profile">
        <form className="admin-form admin-form-grid" onSubmit={saveProfile}>
          <label>
            <span>Full Name</span>
            <input
              type="text"
              value={profileForm.full_name}
              onChange={(event) =>
                updateProfileForm("full_name", event.target.value)
              }
            />
          </label>
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={profileForm.approved}
              onChange={(event) =>
                updateProfileForm("approved", event.target.checked)
              }
            />
            <span>Approved</span>
          </label>
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={profileForm.email_verified}
              onChange={(event) =>
                updateProfileForm("email_verified", event.target.checked)
              }
            />
            <span>Email Verified</span>
          </label>
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={profileForm.active}
              onChange={(event) =>
                updateProfileForm("active", event.target.checked)
              }
            />
            <span>Active</span>
          </label>
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={profileForm.active_in_enterprise}
              onChange={(event) =>
                updateProfileForm("active_in_enterprise", event.target.checked)
              }
            />
            <span>Active in Enterprise</span>
          </label>
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={profileForm.has_admin_privileges}
              onChange={(event) =>
                updateProfileForm("has_admin_privileges", event.target.checked)
              }
            />
            <span>Enterprise Admin Privileges</span>
          </label>
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={profileForm.password_login_disabled}
              onChange={(event) =>
                updateProfileForm(
                  "password_login_disabled",
                  event.target.checked,
                )
              }
            />
            <span>Password Login Disabled</span>
          </label>
          <div className="admin-form-actions admin-fieldset-full">
            <button type="submit" className="admin-primary-button" disabled={saving}>
              {saving ? "Saving…" : "Save Profile"}
            </button>
          </div>
        </form>
      </SectionCard>

      <SectionCard title="Enterprise Assignment">
        <div className="admin-form-grid">
          <label>
            <span>Enterprise</span>
            <select
              value={assignmentEnterpriseId}
              onChange={(event) =>
                setAssignmentEnterpriseId(event.target.value)
              }
            >
              <option value="">Unassigned</option>
              {enterpriseSelectOptions.map((enterprise) => (
                <option key={enterprise.id} value={String(enterprise.id)}>
                  {enterprise.name} #{enterprise.id}
                </option>
              ))}
            </select>
          </label>
          <div className="admin-kv">
            <span>Approval After Assignment</span>
            <strong>Not Approved</strong>
          </div>
          <div className="admin-form-actions admin-fieldset-full">
            <button
              type="button"
              className="admin-primary-button"
              onClick={saveAssignment}
              disabled={saving || !assignmentChanged}
            >
              {saving ? "Saving…" : "Assign Enterprise (Not Approved)"}
            </button>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Audit">
        {auditRows.length ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Status</th>
                <th>Actor</th>
              </tr>
            </thead>
            <tbody>
              {auditRows.map((row) => (
                <tr key={row.id}>
                  <td>{formatDateTime(row.created_at)}</td>
                  <td>{row.action}</td>
                  <td>{row.status}</td>
                  <td>{row.actor_email_id || row.actor_admin_user_id || "n/a"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState title="No user audit rows yet." />
        )}
      </SectionCard>
    </div>
  );
};

export const EnterpriseDetailPage = ({ match, onUnauthorized }) => {
  const enterpriseId = match.params.enterpriseId;
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [enterprise, setEnterprise] = useState(null);
  const [memberForm, setMemberForm] = useState({
    email_id: "",
    full_name: "",
    has_admin_privileges: false,
    password_login_disabled: false,
    sendInvite: true,
  });

  const loadDetail = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await adminRequest(
        "get",
        `/v1/internal-admin/enterprises/${enterpriseId}`,
        null,
        onUnauthorized,
      );
      setEnterprise(response.data.data);
    } catch (error) {
      setError(extractMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // loadDetail is intentionally reused by save/member actions.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadDetail();
  }, [enterpriseId]);

  const updateEnterpriseField = (key, value, section = "root") => {
    setEnterprise((current) => {
      if (!current) {
        return current;
      }
      if (section === "entitlements") {
        return {
          ...current,
          entitlements: {
            ...(current.entitlements || {}),
            [key]: value,
          },
        };
      }
      if (section === "ui_theme") {
        return {
          ...current,
          ui_theme: {
            ...(current.ui_theme || {}),
            [key]: value,
          },
        };
      }
      if (section === "mcp_policy") {
        return {
          ...current,
          mcp_policy: {
            ...(current.mcp_policy || {}),
            [key]: value,
          },
        };
      }
      return {
        ...current,
        [key]: value,
      };
    });
  };

  const saveEnterprise = async () => {
    try {
      setError("");
      const payload = {
        name: enterprise.name,
        archived: enterprise.archived,
        SAML_ENABLED: enterprise.SAML_ENABLED,
        OIDC_ENABLED: enterprise.OIDC_ENABLED,
        OIDC_PROVIDER_TYPE: enterprise.OIDC_PROVIDER_TYPE || "generic",
        OIDC_ISSUER_URL: enterprise.OIDC_ISSUER_URL,
        OIDC_CLIENT_ID: enterprise.OIDC_CLIENT_ID,
        OIDC_SCOPES: enterprise.OIDC_SCOPES,
        OIDC_EMAIL_CLAIM: enterprise.OIDC_EMAIL_CLAIM,
        OIDC_REQUIRE_VERIFIED_EMAIL: enterprise.OIDC_REQUIRE_VERIFIED_EMAIL,
        require_sso_for_sign_in: enterprise.require_sso_for_sign_in,
        SAML_ENTITY_ID: enterprise.SAML_ENTITY_ID,
        SAML_LOGIN_URL: enterprise.SAML_LOGIN_URL,
        SAML_CERTIFICATE: enterprise.SAML_CERTIFICATE,
        email_verification_required: enterprise.email_verification_required,
        auto_approval_on: enterprise.auto_approval_on,
        max_num_seats: enterprise.max_num_seats,
        primary_customer_segment: normalizePrimaryCustomerSegmentValue(
          enterprise.primary_customer_segment,
        ),
        use_case_tags: normalizeUseCaseTagsValue(enterprise.use_case_tags),
        ui_channel: enterprise.ui_channel || "stable",
        ui_theme: enterprise.ui_theme || {},
        entitlements: enterprise.entitlements,
        mcp_policy: {
          enabled: Boolean(enterprise.mcp_policy?.enabled),
          ping_enabled: Boolean(enterprise.mcp_policy?.ping_enabled),
          allowed_scopes: normalizeMcpScopeValue(
            enterprise.mcp_policy?.allowed_scopes || [],
          ),
          notes: enterprise.mcp_policy?.notes || "",
        },
      };
      if (String(enterprise.OIDC_CLIENT_SECRET || "").trim()) {
        payload.OIDC_CLIENT_SECRET = enterprise.OIDC_CLIENT_SECRET;
      }
      await adminRequest(
        "put",
        `/v1/internal-admin/enterprises/${enterpriseId}`,
        payload,
        onUnauthorized,
      );
      await loadDetail();
    } catch (error) {
      setError(extractMessage(error));
    }
  };

  const createMember = async (event) => {
    event.preventDefault();
    try {
      setError("");
      await adminRequest(
        "post",
        `/v1/internal-admin/enterprises/${enterpriseId}/users`,
        {
          ...memberForm,
          password_login_disabled:
            Boolean(enterprise?.require_sso_for_sign_in) ||
            memberForm.password_login_disabled,
          sendInvite: Boolean(enterprise?.require_sso_for_sign_in)
            ? false
            : memberForm.sendInvite,
        },
        onUnauthorized,
      );
      setMemberForm({
        email_id: "",
        full_name: "",
        has_admin_privileges: false,
        password_login_disabled: false,
        sendInvite: true,
      });
      await loadDetail();
    } catch (error) {
      setError(extractMessage(error));
    }
  };

  const updateMember = async (user, changes) => {
    try {
      setError("");
      await adminRequest(
        "put",
        `/v1/internal-admin/users/${user.user_id || user.id}`,
        changes,
        onUnauthorized,
      );
      await loadDetail();
    } catch (error) {
      setError(extractMessage(error));
    }
  };

  const sendInvite = async (user) => {
    try {
      setError("");
      await adminRequest(
        "post",
        `/v1/internal-admin/users/${user.user_id || user.id}/invite`,
        {},
        onUnauthorized,
      );
      await loadDetail();
    } catch (error) {
      setError(extractMessage(error));
    }
  };

  const applyMcpScopePreset = (preset) => {
    updateEnterpriseField("enabled", true, "mcp_policy");
    updateEnterpriseField(
      "allowed_scopes",
      normalizeMcpScopeValue(preset.scopes),
      "mcp_policy",
    );
    updateEnterpriseField("ping_enabled", Boolean(preset.pingEnabled), "mcp_policy");
  };

  if (loading) {
    return <LoadingView />;
  }

  if (!enterprise) {
    return <EmptyState title="Enterprise not found." />;
  }

  const frontendChannels = enterprise.frontendChannels || {};
  const experimentalChannel = frontendChannels.channels?.experimental || {};
  const experimentalAvailable = Boolean(experimentalChannel.available);
  const experimentalServingAvailable = Boolean(
    experimentalChannel.selectable ?? experimentalAvailable,
  );
  const selectedUiChannel = enterprise.ui_channel || "stable";
  const enterpriseRequiresSso = Boolean(enterprise.require_sso_for_sign_in);
  const mcpPolicy = enterprise.mcp_policy || {};
  const mcpScopeOptions = mergeMcpScopeOptions(enterprise.mcp_scope_options);
  const mcpScopeOptionByValue = new Map(
    mcpScopeOptions.map((option) => [option.value, option]),
  );
  const selectedMcpScopes = normalizeMcpScopeValue(
    mcpPolicy.allowed_scopes || [],
  );
  const selectedMcpWriteScopes = selectedMcpScopes.filter((scope) => {
    const option = mcpScopeOptionByValue.get(scope);
    return ["Write", "Serial", "Diagnostics"].includes(option?.category);
  });
  const mcpAuditRows = enterprise.mcp_audit_recent || [];
  const mcpPublicEnterpriseId =
    mcpPolicy.expected_enterprise_id || enterprise.enterprise_id || "n/a";
  const mcpPolicyStatus = mcpPolicy.exists ? "Policy row exists" : "No policy row";
  const frontendChannelNote = experimentalServingAvailable
    ? experimentalChannel.sameAsStable
      ? "Experimental is available and currently matches stable in this deployment. Either channel can be selected."
      : "Experimental is available in this deployment and applies to all users in this enterprise after login or reload."
    : "Experimental can be selected for this enterprise. Stable will be served until an experimental build is deployed.";

  const tabs = [
    ["overview", "Overview"],
    ["auth", "Auth / SSO"],
    ["styling", "Styling"],
    ["entitlements", "Entitlements"],
    ["mcp", "MCP"],
    ["members", "Members"],
    ["devices", "Devices"],
    ["audit", "Audit"],
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>{enterprise.name}</h1>
          <p>
            Enterprise #{enterprise.id} • canonical admin{" "}
            {enterprise.canonicalAdminUser?.email_id || "n/a"}
          </p>
        </div>
        <button type="button" className="admin-primary-button" onClick={saveEnterprise}>
          Save Changes
        </button>
      </div>

      <InlineMessage tone="error">{error}</InlineMessage>

      <div className="admin-tab-row">
        {tabs.map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`admin-tab ${tab === value ? "active" : ""}`}
            onClick={() => setTab(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" ? (
        <SectionCard title="Overview">
          <div className="admin-form-grid">
            <label>
              <span>Name</span>
              <input
                type="text"
                value={enterprise.name || ""}
                onChange={(event) =>
                  updateEnterpriseField("name", event.target.value)
                }
              />
            </label>
            <label>
              <span>Seat Cap</span>
              <input
                type="number"
                value={enterprise.max_num_seats || 0}
                onChange={(event) =>
                  updateEnterpriseField("max_num_seats", Number(event.target.value))
                }
              />
            </label>
            <BusinessTaxonomyFields
              primaryCustomerSegment={enterprise.primary_customer_segment}
              useCaseTags={enterprise.use_case_tags || []}
              onPrimaryCustomerSegmentChange={(value) =>
                updateEnterpriseField("primary_customer_segment", value || null)
              }
              onUseCaseTagsChange={(value) =>
                updateEnterpriseField("use_case_tags", value)
              }
            />
            <label className="admin-checkbox">
              <input
                type="checkbox"
                checked={Boolean(enterprise.archived)}
                onChange={(event) =>
                  updateEnterpriseField("archived", event.target.checked)
                }
              />
              <span>Archived</span>
            </label>
            <label className="admin-checkbox">
              <input
                type="checkbox"
                checked={Boolean(enterprise.auto_approval_on)}
                onChange={(event) =>
                  updateEnterpriseField("auto_approval_on", event.target.checked)
                }
              />
              <span>Auto approval enabled</span>
            </label>
            <label className="admin-checkbox">
              <input
                type="checkbox"
                checked={Boolean(enterprise.email_verification_required)}
                onChange={(event) =>
                  updateEnterpriseField(
                    "email_verification_required",
                    event.target.checked,
                  )
                }
              />
              <span>Email verification required</span>
            </label>
            <label className="admin-fieldset-full">
              <span>Frontend Experience</span>
              <select
                aria-label="Frontend Experience"
                value={selectedUiChannel}
                onChange={(event) =>
                  updateEnterpriseField("ui_channel", event.target.value)
                }
              >
                <option value="stable">Stable</option>
                <option value="experimental">Experimental</option>
              </select>
              <InlineMessage tone="info">{frontendChannelNote}</InlineMessage>
            </label>
          </div>
          <div className="admin-stats-grid">
            <StatCard label="Users" value={enterprise.userCount} />
            <StatCard label="Devices" value={enterprise.deviceCount} />
          </div>
        </SectionCard>
      ) : null}

      {tab === "auth" ? (
        <SectionCard title="Auth / SSO">
          <div className="admin-form-grid">
            <label className="admin-checkbox">
              <input
                type="checkbox"
                checked={Boolean(enterprise.SAML_ENABLED)}
                onChange={(event) =>
                  updateEnterpriseField("SAML_ENABLED", event.target.checked)
                }
              />
              <span>SAML enabled</span>
            </label>
            <label className="admin-checkbox">
              <input
                type="checkbox"
                checked={Boolean(enterprise.OIDC_ENABLED)}
                onChange={(event) =>
                  updateEnterpriseField("OIDC_ENABLED", event.target.checked)
                }
              />
              <span>OIDC enabled</span>
            </label>
            <label className="admin-checkbox">
              <input
                type="checkbox"
                checked={Boolean(enterprise.require_sso_for_sign_in)}
                onChange={(event) =>
                  updateEnterpriseField(
                    "require_sso_for_sign_in",
                    event.target.checked,
                  )
                }
              />
              <span>Require SSO for sign-in</span>
            </label>
            <label className="admin-checkbox">
              <input
                type="checkbox"
                checked={Boolean(enterprise.OIDC_REQUIRE_VERIFIED_EMAIL)}
                onChange={(event) =>
                  updateEnterpriseField(
                    "OIDC_REQUIRE_VERIFIED_EMAIL",
                    event.target.checked,
                  )
                }
              />
              <span>Require verified OIDC email</span>
            </label>
            <label>
              <span>SAML Entity ID</span>
              <input
                type="text"
                value={enterprise.SAML_ENTITY_ID || ""}
                onChange={(event) =>
                  updateEnterpriseField("SAML_ENTITY_ID", event.target.value)
                }
              />
            </label>
            <label>
              <span>SAML Login URL</span>
              <input
                type="text"
                value={enterprise.SAML_LOGIN_URL || ""}
                onChange={(event) =>
                  updateEnterpriseField("SAML_LOGIN_URL", event.target.value)
                }
              />
            </label>
            <label>
              <span>OIDC Provider</span>
              <select
                value={enterprise.OIDC_PROVIDER_TYPE || "generic"}
                onChange={(event) =>
                  updateEnterpriseField("OIDC_PROVIDER_TYPE", event.target.value)
                }
              >
                <option value="generic">Generic OIDC</option>
                <option value="okta">Okta</option>
                <option value="azure">Microsoft Entra ID</option>
              </select>
            </label>
            <label>
              <span>OIDC Issuer URL</span>
              <input
                type="text"
                value={enterprise.OIDC_ISSUER_URL || ""}
                onChange={(event) =>
                  updateEnterpriseField("OIDC_ISSUER_URL", event.target.value)
                }
              />
            </label>
            <label>
              <span>OIDC Client ID</span>
              <input
                type="text"
                value={enterprise.OIDC_CLIENT_ID || ""}
                onChange={(event) =>
                  updateEnterpriseField("OIDC_CLIENT_ID", event.target.value)
                }
              />
            </label>
            <label>
              <span>OIDC Client Secret</span>
              <input
                type="password"
                value={enterprise.OIDC_CLIENT_SECRET || ""}
                placeholder={
                  enterprise.OIDC_CLIENT_SECRET_SET
                    ? "Secret is saved. Enter a new value to rotate."
                    : "Paste client secret"
                }
                autoComplete="new-password"
                onChange={(event) =>
                  updateEnterpriseField("OIDC_CLIENT_SECRET", event.target.value)
                }
              />
            </label>
            <label>
              <span>OIDC Scopes</span>
              <input
                type="text"
                value={enterprise.OIDC_SCOPES || ""}
                onChange={(event) =>
                  updateEnterpriseField("OIDC_SCOPES", event.target.value)
                }
              />
            </label>
            <label>
              <span>OIDC Email Claim</span>
              <input
                type="text"
                value={enterprise.OIDC_EMAIL_CLAIM || ""}
                placeholder="email"
                onChange={(event) =>
                  updateEnterpriseField("OIDC_EMAIL_CLAIM", event.target.value)
                }
              />
            </label>
          </div>
          <label>
            <span>SAML Certificate</span>
            <textarea
              rows={10}
              value={enterprise.SAML_CERTIFICATE || ""}
              onChange={(event) =>
                updateEnterpriseField("SAML_CERTIFICATE", event.target.value)
              }
            />
          </label>
          <div className="admin-stack">
            <div className="admin-kv">
              <span>ACS URL</span>
              <strong>{enterprise.SAML_ACS_URL}</strong>
            </div>
            <div className="admin-kv">
              <span>SP Entity ID</span>
              <strong>{enterprise.SAML_SP_ENTITY_ID}</strong>
            </div>
            <div className="admin-kv">
              <span>OIDC Login URL</span>
              <strong>{enterprise.OIDC_LOGIN_URL || "n/a"}</strong>
            </div>
            <div className="admin-kv">
              <span>OIDC Redirect URI</span>
              <strong>{enterprise.OIDC_REDIRECT_URI || "n/a"}</strong>
            </div>
            <div className="admin-kv">
              <span>OIDC Client Secret</span>
              <strong>{enterprise.OIDC_CLIENT_SECRET_SET ? "Saved" : "Not set"}</strong>
            </div>
          </div>
        </SectionCard>
      ) : null}

      {tab === "styling" ? (
        <EnterpriseStylingPanel
          enterprise={enterprise}
          onUnauthorized={onUnauthorized}
          onThemeChange={(key, value) =>
            updateEnterpriseField(key, value, "ui_theme")
          }
        />
      ) : null}

      {tab === "entitlements" ? (
        <SectionCard title="Entitlements">
          <div className="admin-form-grid">
            <label>
              <span>Max Devices Connected</span>
              <input
                type="number"
                value={enterprise.entitlements?.max_devices_connected || 0}
                onChange={(event) =>
                  updateEnterpriseField(
                    "max_devices_connected",
                    Number(event.target.value),
                    "entitlements",
                  )
                }
              />
            </label>
            <label>
              <span>Logging Depth (days)</span>
              <input
                type="number"
                value={enterprise.entitlements?.custom_logging_depth_in_days || 0}
                onChange={(event) =>
                  updateEnterpriseField(
                    "custom_logging_depth_in_days",
                    Number(event.target.value),
                    "entitlements",
                  )
                }
              />
            </label>
            <label>
              <span>Logging Poll Rate (seconds)</span>
              <input
                type="number"
                value={
                  enterprise.entitlements?.custom_logging_poll_rate_in_seconds || 0
                }
                onChange={(event) =>
                  updateEnterpriseField(
                    "custom_logging_poll_rate_in_seconds",
                    Number(event.target.value),
                    "entitlements",
                  )
                }
              />
            </label>
            <label>
              <span>Cost Per Device (cents)</span>
              <input
                type="number"
                value={enterprise.entitlements?.cost_per_device_in_cents || 0}
                onChange={(event) =>
                  updateEnterpriseField(
                    "cost_per_device_in_cents",
                    Number(event.target.value),
                    "entitlements",
                  )
                }
              />
            </label>
            <label className="admin-checkbox">
              <input
                type="checkbox"
                checked={Boolean(enterprise.entitlements?.custom_logging_enabled)}
                onChange={(event) =>
                  updateEnterpriseField(
                    "custom_logging_enabled",
                    event.target.checked,
                    "entitlements",
                  )
                }
              />
              <span>Enable custom logging</span>
            </label>
            <label className="admin-checkbox">
              <input
                type="checkbox"
                checked={Boolean(
                  enterprise.entitlements?.custom_logging_poll_rate_enabled,
                )}
                onChange={(event) =>
                  updateEnterpriseField(
                    "custom_logging_poll_rate_enabled",
                    event.target.checked,
                    "entitlements",
                  )
                }
              />
              <span>Use custom poll rate</span>
            </label>
          </div>
        </SectionCard>
      ) : null}

      {tab === "mcp" ? (
        <div className="admin-stack">
          <SectionCard
            title="MCP Access"
            subtitle="Tenant policy for remote MCP clients using Synaccess Cloud Personal Access Tokens."
          >
            <div className="admin-kv-grid">
              <div className="admin-kv">
                <span>MCP Tenant Key</span>
                <strong>{mcpPublicEnterpriseId}</strong>
              </div>
              <div className="admin-kv">
                <span>Internal Enterprise ID</span>
                <strong>{mcpPolicy.internal_enterprise_id || enterprise.id}</strong>
              </div>
              <div className="admin-kv">
                <span>Policy Status</span>
                <strong>{mcpPolicyStatus}</strong>
              </div>
              <div className="admin-kv">
                <span>Updated</span>
                <strong>{formatDateTime(mcpPolicy.updated_at)}</strong>
              </div>
            </div>

            {mcpPolicy.uses_legacy_internal_id ? (
              <InlineMessage tone="warning">
                This policy was found with the internal enterprise id. Saving will write the public MCP tenant key.
              </InlineMessage>
            ) : null}

            <div className="admin-form-grid">
              <label className="admin-checkbox">
                <input
                  type="checkbox"
                  checked={Boolean(mcpPolicy.enabled)}
                  onChange={(event) =>
                    updateEnterpriseField(
                      "enabled",
                      event.target.checked,
                      "mcp_policy",
                    )
                  }
                />
                <span>Enable MCP access</span>
              </label>
              <label className="admin-checkbox">
                <input
                  type="checkbox"
                  checked={Boolean(mcpPolicy.ping_enabled)}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    updateEnterpriseField(
                      "ping_enabled",
                      checked,
                      "mcp_policy",
                    );
                    if (
                      checked &&
                      !selectedMcpScopes.includes("mcp.diagnostics.ping")
                    ) {
                      updateEnterpriseField(
                        "allowed_scopes",
                        [...selectedMcpScopes, "mcp.diagnostics.ping"],
                        "mcp_policy",
                      );
                    }
                  }}
                />
                <span>Enable diagnostic ping</span>
              </label>
              <label className="admin-fieldset-full">
                <span>Policy Notes</span>
                <textarea
                  rows={4}
                  value={mcpPolicy.notes || ""}
                  onChange={(event) =>
                    updateEnterpriseField(
                      "notes",
                      event.target.value,
                      "mcp_policy",
                    )
                  }
                />
              </label>
            </div>
          </SectionCard>

          <SectionCard
            title="MCP Presets"
            subtitle="Apply a starting policy, then adjust individual scopes as needed."
          >
            <div className="admin-preset-grid">
              {MCP_SCOPE_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  className="admin-preset-button"
                  onClick={() => applyMcpScopePreset(preset)}
                >
                  <strong>{preset.label}</strong>
                  <span>{preset.description}</span>
                  <small>{preset.scopes.length} scopes</small>
                </button>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="MCP Scopes">
            <div className="admin-scope-grid">
              {mcpScopeOptions.map((option) => (
                <label key={option.value} className="admin-scope-option">
                  <input
                    type="checkbox"
                    checked={selectedMcpScopes.includes(option.value)}
                    onChange={() => {
                      const nextScopes = toggleSelection(
                        selectedMcpScopes,
                        option.value,
                      );
                      updateEnterpriseField(
                        "allowed_scopes",
                        nextScopes,
                        "mcp_policy",
                      );
                      if (
                        option.value === "mcp.diagnostics.ping" &&
                        !nextScopes.includes("mcp.diagnostics.ping")
                      ) {
                        updateEnterpriseField(
                          "ping_enabled",
                          false,
                          "mcp_policy",
                        );
                      }
                    }}
                  />
                  <span>{option.label || option.value}</span>
                  <div className="admin-scope-meta">
                    <TagPill>{option.category || "Scope"}</TagPill>
                    <TagPill tone={mcpRiskTone(option.risk)}>
                      {formatMcpRiskLabel(option.risk)}
                    </TagPill>
                  </div>
                  <small>{option.value}</small>
                  {option.description ? (
                    <p>{option.description}</p>
                  ) : null}
                </label>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="MCP Safety Summary">
            <div className="admin-two-column">
              <div className="admin-list-card">
                <strong>Selected sensitive scopes</strong>
                {selectedMcpWriteScopes.length ? (
                  <div className="admin-tag-list">
                    {selectedMcpWriteScopes.map((scope) => {
                      const option = mcpScopeOptionByValue.get(scope);
                      return (
                        <TagPill
                          key={scope}
                          tone={mcpRiskTone(option?.risk)}
                        >
                          {option?.label || scope}
                        </TagPill>
                      );
                    })}
                  </div>
                ) : (
                  <p>No write, serial, or diagnostic scopes selected.</p>
                )}
              </div>
              <div className="admin-list-card">
                <strong>Operational boundaries</strong>
                <ul className="admin-compact-list">
                  <li>Personal Access Token user permissions still apply.</li>
                  <li>Write tools are audited with target and outcome metadata.</li>
                  <li>Device group management does not expose outlet groups or group user membership.</li>
                  <li>Serial console requires a trusted admin PAT and sticky routing or one MCP instance.</li>
                  <li>Configuration writes are allowlisted and do not expose raw network, user, firmware, or unpair changes.</li>
                </ul>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Recent MCP Tool Audit">
            {mcpAuditRows.length ? (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Tool</th>
                    <th>User</th>
                    <th>Target</th>
                    <th>Outcome</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {mcpAuditRows.map((row) => (
                    <tr key={row.id}>
                      <td>{formatDateTime(row.created_at)}</td>
                      <td>{row.tool_name}</td>
                      <td>{row.user_id || "n/a"}</td>
                      <td>
                        {row.enclosure_serial_number || "n/a"}
                        {row.target_kind ? ` / ${row.target_kind}` : ""}
                        {row.target_resource ? ` / ${row.target_resource}` : ""}
                      </td>
                      <td>{row.outcome}</td>
                      <td>{row.error_code || "none"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <EmptyState
                title="No MCP audit rows yet."
                description="Sensitive MCP tool attempts will appear here after the tenant starts using diagnostics or writes."
              />
            )}
          </SectionCard>
        </div>
      ) : null}

      {tab === "members" ? (
        <div className="admin-stack">
          <SectionCard title="Add Member">
            <form className="admin-form admin-form-grid admin-member-create-form" onSubmit={createMember}>
              <label>
                <span>Email</span>
                <input
                  type="email"
                  value={memberForm.email_id}
                  onChange={(event) =>
                    setMemberForm((current) => ({
                      ...current,
                      email_id: event.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label>
                <span>Full Name</span>
                <input
                  type="text"
                  value={memberForm.full_name}
                  onChange={(event) =>
                    setMemberForm((current) => ({
                      ...current,
                      full_name: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="admin-checkbox">
                <input
                  type="checkbox"
                  checked={memberForm.has_admin_privileges}
                  onChange={(event) =>
                    setMemberForm((current) => ({
                      ...current,
                      has_admin_privileges: event.target.checked,
                    }))
                  }
                />
                <span>Grant enterprise admin privileges</span>
              </label>
              <label className="admin-checkbox">
                <input
                  type="checkbox"
                  checked={
                    enterpriseRequiresSso ||
                    memberForm.password_login_disabled
                  }
                  onChange={(event) =>
                    setMemberForm((current) => ({
                      ...current,
                      password_login_disabled: event.target.checked,
                    }))
                  }
                  disabled={enterpriseRequiresSso}
                />
                <span>Disable password login</span>
              </label>
              <label className="admin-checkbox">
                <input
                  type="checkbox"
                  checked={!enterpriseRequiresSso && memberForm.sendInvite}
                  onChange={(event) =>
                    setMemberForm((current) => ({
                      ...current,
                      sendInvite: event.target.checked,
                    }))
                  }
                  disabled={enterpriseRequiresSso}
                />
                <span>Send invite email immediately</span>
              </label>
              <div className="admin-member-create-actions">
                <button type="submit" className="admin-primary-button">
                  Add Member
                </button>
              </div>
            </form>
          </SectionCard>

          <SectionCard title="Members">
            {enterprise.users?.length ? (
              <div className="admin-table-shell">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Admin</th>
                      <th>Approved</th>
                      <th>Email Verified</th>
                      <th>Active</th>
                      <th>Password Login Disabled</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enterprise.users.map((user) => (
                      <tr key={user.user_id || user.id}>
                        <td>
                          <div className="admin-member-identity">
                            <strong>{user.email_id}</strong>
                          </div>
                        </td>
                        <td>{user.full_name || "No name set"}</td>
                        <td>
                          <div className="admin-checkbox-cell">
                            <input
                              aria-label={`Toggle admin privileges for ${user.email_id}`}
                              type="checkbox"
                              checked={Boolean(user.has_admin_privileges)}
                              onChange={(event) =>
                                updateMember(user, {
                                  has_admin_privileges: event.target.checked,
                                })
                              }
                            />
                          </div>
                        </td>
                        <td>
                          <div className="admin-checkbox-cell">
                            <input
                              aria-label={`Toggle approved for ${user.email_id}`}
                              type="checkbox"
                              checked={Boolean(user.approved)}
                              onChange={(event) =>
                                updateMember(user, { approved: event.target.checked })
                              }
                            />
                          </div>
                        </td>
                        <td>
                          <div className="admin-checkbox-cell">
                            <input
                              aria-label={`Toggle email verified for ${user.email_id}`}
                              type="checkbox"
                              checked={Boolean(user.email_verified)}
                              onChange={(event) =>
                                updateMember(user, {
                                  email_verified: event.target.checked,
                                })
                              }
                            />
                          </div>
                        </td>
                        <td>
                          <div className="admin-checkbox-cell">
                            <input
                              aria-label={`Toggle active in enterprise for ${user.email_id}`}
                              type="checkbox"
                              checked={Boolean(user.active_in_enterprise)}
                              onChange={(event) =>
                                updateMember(user, {
                                  active_in_enterprise: event.target.checked,
                                })
                              }
                            />
                          </div>
                        </td>
                        <td>
                          <div className="admin-checkbox-cell">
                            <input
                              aria-label={`Toggle password login disabled for ${user.email_id}`}
                              type="checkbox"
                              checked={
                                enterpriseRequiresSso ||
                                Boolean(user.password_login_disabled)
                              }
                              onChange={(event) =>
                                updateMember(user, {
                                  password_login_disabled: event.target.checked,
                                })
                              }
                              disabled={enterpriseRequiresSso}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="admin-inline-actions admin-member-actions">
                            <button
                              type="button"
                              className="admin-secondary-button"
                              onClick={() => sendInvite(user)}
                              disabled={
                                enterpriseRequiresSso ||
                                Boolean(user.password_login_disabled)
                              }
                            >
                              Send Invite
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState title="No enterprise users yet." />
            )}
          </SectionCard>
        </div>
      ) : null}

      {tab === "devices" ? (
        <SectionCard title="Devices">
          {enterprise.devices?.length ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Serial</th>
                  <th>Name</th>
                  <th>Model</th>
                  <th>Connection</th>
                  <th>Firmware</th>
                </tr>
              </thead>
              <tbody>
                {enterprise.devices.map((device) => (
                  <tr key={device.enclosureSerialNumber}>
                    <td>
                      <NavLink to={`/admin/devices/${device.enclosureSerialNumber}`}>
                        {device.enclosureSerialNumber}
                      </NavLink>
                    </td>
                    <td>{device.deviceName || "n/a"}</td>
                    <td>{device.modelNumber || "n/a"}</td>
                    <td>{device.connectionState}</td>
                    <td>
                      {device.controllerFirmwareVersion || "n/a"}
                      {device.updateAvailable ? " -> update available" : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyState title="No devices assigned to this enterprise." />
          )}
        </SectionCard>
      ) : null}

      {tab === "audit" ? (
        <SectionCard title="Recent Enterprise Audit">
          {enterprise.audit?.length ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Action</th>
                  <th>Status</th>
                  <th>Target</th>
                </tr>
              </thead>
              <tbody>
                {enterprise.audit.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDateTime(row.created_at)}</td>
                    <td>{row.action}</td>
                    <td>{row.status}</td>
                    <td>{row.target_type}:{row.target_id || "n/a"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyState title="No enterprise audit rows yet." />
          )}
        </SectionCard>
      ) : null}
    </div>
  );
};

const FirmwareCatalogListPage = ({ history, onUnauthorized }) => {
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async (nextSearch = "") => {
    setLoading(true);
    setError("");
    try {
      const response = await adminRequest(
        "get",
        "/v1/internal-admin/firmware-catalog",
        null,
        onUnauthorized,
        {
          params: {
            search: nextSearch,
          },
        },
      );
      setRows(response.data.data || []);
    } catch (error) {
      setError(extractMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, []);

  const handleSearch = (event) => {
    event.preventDefault();
    load(search.trim());
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Firmware Catalog</h1>
          <p>Manage the deployment firmware map now stored in the database.</p>
        </div>
        <button
          type="button"
          className="admin-primary-button"
          onClick={() => history.push("/admin/firmware-catalog/new")}
        >
          New Catalog Record
        </button>
      </div>

      <SectionCard title="Search">
        <form className="admin-search-form" onSubmit={handleSearch}>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by model, hardware version, enterprise scope, version"
          />
          <button type="submit" className="admin-primary-button">
            Apply
          </button>
        </form>
        <InlineMessage tone="error">{error}</InlineMessage>
      </SectionCard>

      <SectionCard title="Catalog Records">
        {loading ? <LoadingView /> : null}
        {!loading && !rows.length ? (
          <EmptyState title="No firmware catalog records found." />
        ) : null}
        {!loading && rows.length ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Scope</th>
                <th>Models</th>
                <th>Hardware</th>
                <th>Versions</th>
                <th>Impacted Devices</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="clickable"
                  onClick={() => history.push(`/admin/firmware-catalog/${row.id}`)}
                >
                  <td>{row.id}</td>
                  <td>{(row.enterprise_scope || []).join(", ")}</td>
                  <td>{(row.models || []).join(", ")}</td>
                  <td>{row.hardware_version}</td>
                  <td>
                    {(row.available_firmware || [])
                      .map(formatFirmwareVersionEntry)
                      .filter(Boolean)
                      .join(", ")}
                  </td>
                  <td>{row.impacted_device_count ?? "n/a"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </SectionCard>
    </div>
  );
};

export const FirmwareAssetUploadCard = ({ onUnauthorized }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadNotice, setUploadNotice] = useState("");
  const [uploadedAsset, setUploadedAsset] = useState(null);

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files?.[0] || null);
    setUploadError("");
    setUploadNotice("");
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError("Select a .swu file to upload.");
      return;
    }

    if (!selectedFile.name.toLowerCase().endsWith(".swu")) {
      setUploadError("Firmware uploads must use a .swu file.");
      return;
    }

    setUploading(true);
    setUploadError("");
    setUploadNotice("");

    try {
      const payload = new FormData();
      payload.append("file", selectedFile);

      const response = await adminRequest(
        "post",
        "/v1/internal-admin/firmware-assets/upload",
        payload,
        onUnauthorized,
      );

      setUploadedAsset(response.data.data || null);
      setUploadNotice(
        "Upload complete. Paste the returned URL into available_firmware[].url.",
      );
    } catch (error) {
      setUploadError(extractMessage(error));
    } finally {
      setUploading(false);
    }
  };

  const handleCopyUrl = async () => {
    if (!uploadedAsset?.url) {
      return;
    }

    try {
      await copyTextToClipboard(uploadedAsset.url);
      setUploadNotice("Uploaded asset URL copied to the clipboard.");
    } catch (error) {
      setUploadError(extractMessage(error));
    }
  };

  return (
    <SectionCard
      title="Upload Firmware Asset (.swu)"
      subtitle="Upload a firmware blob to Azure Storage and then paste the returned URL into available_firmware[].url."
    >
      <div className="admin-form">
        <InlineMessage tone="error">{uploadError}</InlineMessage>
        <InlineMessage tone="info">{uploadNotice}</InlineMessage>
        <label>
          <span>Firmware Asset File</span>
          <input
            type="file"
            accept=".swu"
            onChange={handleFileChange}
          />
        </label>
        <div className="admin-upload-meta">
          <strong>{selectedFile?.name || "No file selected."}</strong>
          {selectedFile ? <span>{formatFileSize(selectedFile.size)}</span> : null}
        </div>
        <div className="admin-form-actions">
          <button
            type="button"
            className="admin-primary-button"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
          >
            {uploading ? "Uploading…" : "Upload .swu"}
          </button>
        </div>
      </div>

      {uploadedAsset ? (
        <div className="admin-upload-result">
          <label>
            <span>Uploaded Asset URL</span>
            <input
              type="text"
              readOnly
              value={uploadedAsset.url || ""}
            />
          </label>
          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-secondary-button"
              onClick={handleCopyUrl}
            >
              Copy URL
            </button>
          </div>
          <div className="admin-kv-grid">
            <div className="admin-kv">
              <span>Blob Name</span>
              <strong>{uploadedAsset.blobName || "n/a"}</strong>
            </div>
            <div className="admin-kv">
              <span>Size</span>
              <strong>{formatFileSize(uploadedAsset.sizeBytes)}</strong>
            </div>
            <div className="admin-kv">
              <span>Uploaded</span>
              <strong>{formatDateTime(uploadedAsset.uploadedAt)}</strong>
            </div>
          </div>
        </div>
      ) : null}
    </SectionCard>
  );
};

export const FirmwareCatalogEditorPage = ({ match, history, onUnauthorized }) => {
  const catalogId = match.params.catalogId;
  const creating = !catalogId;
  const [loading, setLoading] = useState(!creating);
  const [error, setError] = useState("");
  const [validationResult, setValidationResult] = useState(null);
  const [form, setForm] = useState({
    enterprise_scope: "*",
    models: "",
    hardware_version: "",
    available_firmware: JSON.stringify([], null, 2),
  });

  useEffect(() => {
    if (creating) {
      return;
    }

    let active = true;
    const load = async () => {
      try {
        const response = await adminRequest(
          "get",
          `/v1/internal-admin/firmware-catalog/${catalogId}`,
          null,
          onUnauthorized,
        );
        if (!active) {
          return;
        }
        const row = response.data.data;
        setForm({
          enterprise_scope: (row.enterprise_scope || []).join(", "),
          models: (row.models || []).join(", "),
          hardware_version: row.hardware_version || "",
          available_firmware: JSON.stringify(row.available_firmware || [], null, 2),
        });
      } catch (error) {
        if (!active) {
          return;
        }
        setError(extractMessage(error));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [catalogId, creating, onUnauthorized]);

  const buildPayload = () => ({
    enterprise_scope: form.enterprise_scope
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean),
    models: form.models
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean),
    hardware_version: form.hardware_version.trim(),
    available_firmware: parseJsonInput(form.available_firmware),
  });

  const handleSave = async (event) => {
    event.preventDefault();
    setError("");
    try {
      const payload = buildPayload();
      if (creating) {
        const response = await adminRequest(
          "post",
          "/v1/internal-admin/firmware-catalog",
          payload,
          onUnauthorized,
        );
        history.push(`/admin/firmware-catalog/${response.data.data.id}`);
      } else {
        await adminRequest(
          "put",
          `/v1/internal-admin/firmware-catalog/${catalogId}`,
          payload,
          onUnauthorized,
        );
      }
    } catch (error) {
      setError(extractMessage(error));
    }
  };

  const handleValidate = async () => {
    setError("");
    try {
      const response = await adminRequest(
        "post",
        `/v1/internal-admin/firmware-catalog/${catalogId || "new"}/validate`,
        buildPayload(),
        onUnauthorized,
      );
      setValidationResult(response.data.data);
    } catch (error) {
      setValidationResult(error?.response?.data?.data || null);
      setError(extractMessage(error));
    }
  };

  const handleArchive = async () => {
    if (creating) {
      return;
    }
    try {
      setError("");
      await adminRequest(
        "delete",
        `/v1/internal-admin/firmware-catalog/${catalogId}`,
        null,
        onUnauthorized,
      );
      history.push("/admin/firmware-catalog");
    } catch (error) {
      setError(extractMessage(error));
    }
  };

  if (loading) {
    return <LoadingView />;
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>{creating ? "New Firmware Catalog Record" : `Catalog #${catalogId}`}</h1>
          <p>Manage enterprise scope, compatible models, and version graph.</p>
        </div>
        {!creating ? (
          <button type="button" className="admin-danger-button" onClick={handleArchive}>
            Archive Record
          </button>
        ) : null}
      </div>

      <FirmwareAssetUploadCard onUnauthorized={onUnauthorized} />

      <SectionCard title="Catalog Editor">
        <form className="admin-form" onSubmit={handleSave}>
          <InlineMessage tone="error">{error}</InlineMessage>
          <label>
            <span>Enterprise Scope</span>
            <input
              type="text"
              value={form.enterprise_scope}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  enterprise_scope: event.target.value,
                }))
              }
              placeholder="*, 16, 22"
            />
          </label>
          <label>
            <span>Models</span>
            <input
              type="text"
              value={form.models}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  models: event.target.value,
                }))
              }
              placeholder="NP-0201DX, NP-0202DX"
            />
          </label>
          <label>
            <span>Hardware Version</span>
            <input
              type="text"
              value={form.hardware_version}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  hardware_version: event.target.value,
                }))
              }
            />
          </label>
          <label>
            <span>Available Firmware JSON</span>
            <textarea
              rows={18}
              value={form.available_firmware}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  available_firmware: event.target.value,
                }))
              }
            />
          </label>
          <div className="admin-form-actions">
            <button type="submit" className="admin-primary-button">
              {creating ? "Create Record" : "Save Record"}
            </button>
            <button
              type="button"
              className="admin-secondary-button"
              onClick={handleValidate}
            >
              Validate
            </button>
          </div>
        </form>
        {validationResult ? (
          <JsonBlock value={validationResult} />
        ) : null}
      </SectionCard>
    </div>
  );
};

export const DeviceDebuggerPage = ({ match, onUnauthorized }) => {
  const serial = match.params.serial;
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  const [activeEndpoint, setActiveEndpoint] = useState("device");
  const [livePayload, setLivePayload] = useState(null);
  const [logsMode, setLogsMode] = useState("unified");
  const [logsPayload, setLogsPayload] = useState(null);
  const [telemetryMode, setTelemetryMode] = useState("outlets");
  const [telemetryPayload, setTelemetryPayload] = useState(null);
  const [pairState, setPairState] = useState(buildInitialDevicePairState);
  const [pairPolling, setPairPolling] = useState(null);
  const [toolState, setToolState] = useState({
    deviceGetPath: "/api/device",
    pingHost: "",
    result: null,
  });

  const loadContext = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const response = await adminRequest(
        "get",
        `/v1/internal-admin/devices/${serial}/context`,
        null,
        onUnauthorized,
      );
      setContext(response.data.data);
      return response.data.data;
    } catch (error) {
      if (!silent) {
        setError(extractMessage(error));
      }
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setPairState(buildInitialDevicePairState());
    setPairPolling(null);
    loadContext();
  }, [serial]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pairPolling) {
      return undefined;
    }

    if (pairPolling.remainingAttempts <= 0) {
      setPairState((current) => ({
        ...current,
        info:
          pairPolling.mode === "accepted"
            ? `Pair request accepted for ${pairPolling.canonicalAdminEmail}, but ownership has not shown up yet. Refresh and verify before retrying.`
            : `Could not confirm pairing for ${pairPolling.canonicalAdminEmail}. The device may have restarted before IoT Hub confirmed the request. Refresh and verify before retrying.`,
      }));
      setPairPolling(null);
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      const nextContext = await loadContext({ silent: true });
      if (!nextContext) {
        setPairPolling((current) =>
          current
            ? {
                ...current,
                remainingAttempts: current.remainingAttempts - 1,
              }
            : current,
        );
        return;
      }

      const enterpriseMatches =
        String(nextContext.enterprise?.id || "") ===
        String(pairPolling.enterpriseId || "");
      const canonicalAdminMatches =
        String(nextContext.canonicalAdminUser?.id || "") ===
        String(pairPolling.canonicalAdminUserId || "");

      if (nextContext.isPaired && enterpriseMatches && canonicalAdminMatches) {
        setPairState((current) => ({
          ...current,
          error: "",
          info: `Device paired to ${pairPolling.canonicalAdminEmail}.`,
        }));
        setPairPolling(null);
        return;
      }

      setPairPolling((current) =>
        current
          ? {
              ...current,
              remainingAttempts: current.remainingAttempts - 1,
            }
          : current,
      );
    }, DEVICE_PAIR_POLL_INTERVAL_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [pairPolling, serial]);

  const fetchLiveEndpoint = async (endpoint) => {
    setActiveEndpoint(endpoint);
    setError("");
    try {
      const response = await adminRequest(
        "get",
        `/v1/internal-admin/devices/${serial}/live/${endpoint}`,
        null,
        onUnauthorized,
      );
      setLivePayload(response.data.data);
    } catch (error) {
      setError(extractMessage(error));
    }
  };

  const fetchLogs = async (mode) => {
    setLogsMode(mode);
    setError("");
    try {
      const response = await adminRequest(
        "get",
        `/v1/internal-admin/devices/${serial}/logs/${mode}`,
        null,
        onUnauthorized,
      );
      setLogsPayload(response.data.data);
    } catch (error) {
      setError(extractMessage(error));
    }
  };

  const fetchTelemetry = async (mode) => {
    setTelemetryMode(mode);
    setError("");
    try {
      const response = await adminRequest(
        "get",
        `/v1/internal-admin/devices/${serial}/telemetry/${mode}`,
        null,
        onUnauthorized,
      );
      setTelemetryPayload(response.data.data);
    } catch (error) {
      setError(extractMessage(error));
    }
  };

  // Tab hydration is intentionally driven only by tab switches.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === "live" && livePayload === null) {
      fetchLiveEndpoint(activeEndpoint);
    }
    if (tab === "logs" && logsPayload === null) {
      fetchLogs(logsMode);
    }
    if (tab === "telemetry" && telemetryPayload === null) {
      fetchTelemetry(telemetryMode);
    }
  }, [tab]);

  const runDeviceGet = async () => {
    try {
      const response = await adminRequest(
        "post",
        `/v1/internal-admin/devices/${serial}/methods/get`,
        {
          path: toolState.deviceGetPath,
        },
        onUnauthorized,
      );
      setToolState((current) => ({
        ...current,
        result: response.data.data,
      }));
    } catch (error) {
      setError(extractMessage(error));
    }
  };

  const runPing = async () => {
    try {
      const response = await adminRequest(
        "post",
        `/v1/internal-admin/devices/${serial}/actions/ping`,
        {
          host: toolState.pingHost,
        },
        onUnauthorized,
      );
      setToolState((current) => ({
        ...current,
        result: response.data.data,
      }));
    } catch (error) {
      setError(extractMessage(error));
    }
  };

  const runReboot = async () => {
    try {
      const response = await adminRequest(
        "post",
        `/v1/internal-admin/devices/${serial}/actions/reboot`,
        {},
        onUnauthorized,
      );
      setToolState((current) => ({
        ...current,
        result: response.data.data,
      }));
    } catch (error) {
      setError(extractMessage(error));
    }
  };

  const startCliSession = async () => {
    try {
      const response = await adminRequest(
        "post",
        `/v1/internal-admin/devices/${serial}/cli/session`,
        {},
        onUnauthorized,
      );
      setToolState((current) => ({
        ...current,
        result: response.data.data,
      }));
    } catch (error) {
      setError(extractMessage(error));
    }
  };

  const searchEnterprisesForPairing = async (event) => {
    event?.preventDefault();

    setPairState((current) => ({
      ...current,
      loadingSearch: true,
      searched: true,
      error: "",
    }));

    try {
      const response = await adminRequest(
        "get",
        "/v1/internal-admin/enterprises",
        null,
        onUnauthorized,
        {
          params: {
            search: pairState.search.trim(),
            limit: DEVICE_PAIR_SEARCH_LIMIT,
          },
        },
      );
      const enterpriseOptions = Array.isArray(response.data?.data)
        ? response.data.data
        : [];

      setPairState((current) => ({
        ...current,
        loadingSearch: false,
        searched: true,
        error: "",
        enterpriseOptions,
        selectedEnterpriseId: enterpriseOptions.some(
          (enterprise) =>
            String(enterprise.id) === String(current.selectedEnterpriseId),
        )
          ? current.selectedEnterpriseId
          : String(enterpriseOptions[0]?.id || ""),
      }));
    } catch (error) {
      setPairState((current) => ({
        ...current,
        loadingSearch: false,
        searched: true,
        error: extractMessage(error),
      }));
    }
  };

  const selectedEnterprise = pairState.enterpriseOptions.find(
    (enterprise) =>
      String(enterprise.id) === String(pairState.selectedEnterpriseId),
  );
  const selectedCanonicalAdminEmail =
    selectedEnterprise?.canonicalAdminUser?.email_id || "";
  const isPairConfirmationPending = Boolean(pairPolling);

  const startPairConfirmation = ({
    enterpriseId,
    canonicalAdminUserId,
    canonicalAdminEmail,
    mode = "accepted",
    info,
  }) => {
    setPairState((current) => ({
      ...current,
      submitting: false,
      error: "",
      info,
    }));
    setPairPolling(
      buildPairPollingState({
        enterpriseId,
        canonicalAdminUserId,
        canonicalAdminEmail,
        mode,
      }),
    );
  };

  const submitPairRequest = async () => {
    if (!selectedEnterprise) {
      setPairState((current) => ({
        ...current,
        error: "Select an enterprise before pairing the device.",
      }));
      return;
    }

    setPairState((current) => ({
      ...current,
      submitting: true,
      error: "",
      info: "",
    }));

    try {
      const response = await adminRequest(
        "post",
        `/v1/internal-admin/devices/${serial}/pair`,
        {
          enterpriseId: selectedEnterprise.id,
        },
        onUnauthorized,
      );
      const pairResult = response.data.data;

      startPairConfirmation({
        enterpriseId: String(pairResult.enterpriseId),
        canonicalAdminUserId: String(pairResult.canonicalAdminUserId),
        canonicalAdminEmail: pairResult.canonicalAdminEmail,
        mode: "accepted",
        info: `Pairing in progress for ${pairResult.canonicalAdminEmail}. Checking ownership now.`,
      });
    } catch (error) {
      const ambiguousOffline = extractPairingOfflineAmbiguity(error);
      if (ambiguousOffline && selectedEnterprise) {
        startPairConfirmation({
          enterpriseId: String(selectedEnterprise.id),
          canonicalAdminUserId: String(
            selectedEnterprise.canonicalAdminUser?.id || "",
          ),
          canonicalAdminEmail: selectedCanonicalAdminEmail,
          mode: "uncertain",
          info:
            "IoT Hub could not confirm the pair request because the device went offline mid-flight. The controller may have restarted during pairing, so ownership is being checked now.",
        });
        return;
      }

      setPairState((current) => ({
        ...current,
        submitting: false,
        error: extractMessage(error),
      }));
    }
  };

  if (loading) {
    return <LoadingView />;
  }

  if (!context) {
    return <EmptyState title="Device not found." />;
  }

  const tabs = [
    ["overview", "Overview"],
    ["live", "Live"],
    ["logs", "Logs"],
    ["telemetry", "Telemetry"],
    ["tools", "Tools"],
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Device {context.serial}</h1>
          <p>
            {context.device?.last_known_name || context.device?.deviceName || "Unnamed"} •{" "}
            {context.enterprise?.name || "No enterprise"}
          </p>
        </div>
      </div>

      <InlineMessage tone="error">{error}</InlineMessage>
      <InlineMessage tone="info">{pairState.info}</InlineMessage>

      <div className="admin-tab-row">
        {tabs.map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`admin-tab ${tab === value ? "active" : ""}`}
            onClick={() => setTab(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" ? (
        <SectionCard title="Device Context">
          <div className="admin-kv-grid">
            <div className="admin-kv">
              <span>Enterprise</span>
              <strong>{context.enterprise?.name || "n/a"}</strong>
            </div>
            <div className="admin-kv">
              <span>Canonical Admin</span>
              <strong>{context.canonicalAdminUser?.email_id || "n/a"}</strong>
            </div>
            <div className="admin-kv">
              <span>Paired</span>
              <strong>{context.isPaired ? "Yes" : "No"}</strong>
            </div>
            <div className="admin-kv">
              <span>Azure Device ID</span>
              <strong>{context.device?.azure_device_id || "n/a"}</strong>
            </div>
            <div className="admin-kv">
              <span>Connection</span>
              <strong>{context.connectionState}</strong>
            </div>
            <div className="admin-kv">
              <span>Last Heard</span>
              <strong>{formatDateTime(context.lastHeard)}</strong>
            </div>
            <div className="admin-kv">
              <span>Firmware</span>
              <strong>
                {context.currentFirmwareVersion || "n/a"} / latest{" "}
                {context.latestFirmwareVersion || "n/a"}
              </strong>
            </div>
            <div className="admin-kv">
              <span>Active Alerts</span>
              <strong>{context.activeAlertCount || 0}</strong>
            </div>
            <div className="admin-kv">
              <span>Catalog Match</span>
              <strong>{context.firmwareCatalogRecord?.id || "none"}</strong>
            </div>
          </div>
        </SectionCard>
      ) : null}

      {tab === "overview" && !context.isPaired ? (
        <SectionCard
          title="Pair to Enterprise"
          subtitle="This uses the selected enterprise's canonical admin cloud token and a server-signed JWT."
        >
          <div className="admin-form">
            <InlineMessage tone="error">{pairState.error}</InlineMessage>
            <InlineMessage tone="info">
              {context.connectionState !== "Connected"
                ? "This device is currently marked disconnected by the heartbeat cache. That signal can be stale, so you can still try pairing and let the device method response decide."
                : !context.device?.azure_device_id
                  ? "This device is missing an Azure Device ID, so cloud pairing cannot be sent."
                  : "Search for the target enterprise, then confirm the canonical admin owner before sending the pairing request. Pairing can briefly restart the device's cloud agent while confirmation catches up."}
            </InlineMessage>

            <form className="admin-form" onSubmit={searchEnterprisesForPairing}>
              <label>
                <span>Search Enterprises</span>
                <input
                  type="text"
                  value={pairState.search}
                  onChange={(event) =>
                    setPairState((current) => ({
                      ...current,
                      search: event.target.value,
                    }))
                  }
                  placeholder="Name or enterprise ID"
                />
              </label>
              <div className="admin-form-actions">
                <button
                  type="submit"
                  className="admin-secondary-button"
                  disabled={pairState.loadingSearch || isPairConfirmationPending}
                >
                  {pairState.loadingSearch ? "Searching..." : "Search"}
                </button>
              </div>
            </form>

            {pairState.enterpriseOptions.length ? (
              <label>
                <span>Enterprise</span>
                <select
                  value={pairState.selectedEnterpriseId}
                  onChange={(event) =>
                    setPairState((current) => ({
                      ...current,
                      selectedEnterpriseId: event.target.value,
                    }))
                  }
                >
                  {pairState.enterpriseOptions.map((enterprise) => (
                    <option key={enterprise.id} value={enterprise.id}>
                      {enterprise.name} ({enterprise.id})
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {pairState.searched && !pairState.enterpriseOptions.length ? (
              <EmptyState
                title="No enterprises found."
                description="Try a different name or enterprise ID."
              />
            ) : null}

            {selectedEnterprise ? (
              <div className="admin-kv-grid">
                <div className="admin-kv">
                  <span>Canonical Admin Owner</span>
                  <strong>{selectedCanonicalAdminEmail || "Missing canonical admin"}</strong>
                </div>
                <div className="admin-kv">
                  <span>Enterprise</span>
                  <strong>{selectedEnterprise.name}</strong>
                </div>
              </div>
            ) : null}

            <div className="admin-form-actions">
              <button
                type="button"
                className="admin-primary-button"
                disabled={
                  pairState.submitting ||
                  isPairConfirmationPending ||
                  !selectedEnterprise ||
                  !selectedCanonicalAdminEmail ||
                  !context.device?.azure_device_id
                }
                onClick={submitPairRequest}
              >
                {pairState.submitting
                  ? "Pairing..."
                  : isPairConfirmationPending
                    ? "Checking Pair Status..."
                    : "Pair to Enterprise"}
              </button>
            </div>
          </div>
        </SectionCard>
      ) : null}

      {tab === "live" ? (
        <SectionCard
          title="Live GET Snapshots"
          actions={
            <div className="admin-pill-row">
              {[
                "device",
                "conf",
                "outlets",
                "inlets",
                "banks",
                "sensors",
                "events",
                "actions",
                "network/vlans",
                "network/routes",
                "network/wan-health",
              ].map((endpoint) => (
                <button
                  key={endpoint}
                  type="button"
                  className={`admin-pill ${activeEndpoint === endpoint ? "active" : ""}`}
                  onClick={() => fetchLiveEndpoint(endpoint)}
                >
                  {endpoint}
                </button>
              ))}
            </div>
          }
        >
          {livePayload ? <JsonBlock value={livePayload} /> : <LoadingView />}
        </SectionCard>
      ) : null}

      {tab === "logs" ? (
        <SectionCard
          title="Logs"
          actions={
            <div className="admin-pill-row">
              {[
                ["unified", "Unified"],
                ["event-history", "Event History"],
                ["activity", "Activity"],
                ["event-state", "Event State"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`admin-pill ${logsMode === value ? "active" : ""}`}
                  onClick={() => fetchLogs(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          }
        >
          {logsPayload ? <JsonBlock value={logsPayload} /> : <LoadingView />}
        </SectionCard>
      ) : null}

      {tab === "telemetry" ? (
        <SectionCard
          title="Telemetry"
          actions={
            <div className="admin-pill-row">
              {[
                ["outlets", "Outlets"],
                ["inlets", "Inlets"],
                ["sensors/temp-hum", "Temp / Hum"],
                ["sensors/dio-inputs", "DIO Inputs"],
                ["sensors/dio-relays", "DIO Relays"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`admin-pill ${telemetryMode === value ? "active" : ""}`}
                  onClick={() => fetchTelemetry(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          }
        >
          {telemetryPayload ? <JsonBlock value={telemetryPayload} /> : <LoadingView />}
        </SectionCard>
      ) : null}

      {tab === "tools" ? (
        <div className="admin-two-column">
          <SectionCard title="Read Only Device GET">
            <div className="admin-form">
              <label>
                <span>Allowlisted path</span>
                <select
                  value={toolState.deviceGetPath}
                  onChange={(event) =>
                    setToolState((current) => ({
                      ...current,
                      deviceGetPath: event.target.value,
                    }))
                  }
                >
                  {ALLOWED_DEVICE_GET_PATHS.map((path) => (
                    <option key={path} value={path}>
                      {path}
                    </option>
                  ))}
                </select>
              </label>
              <button type="button" className="admin-primary-button" onClick={runDeviceGet}>
                Execute GET
              </button>
            </div>
          </SectionCard>

          <SectionCard title="Support Actions">
            <div className="admin-form">
              <label>
                <span>Ping Host</span>
                <input
                  type="text"
                  value={toolState.pingHost}
                  onChange={(event) =>
                    setToolState((current) => ({
                      ...current,
                      pingHost: event.target.value,
                    }))
                  }
                  placeholder="8.8.8.8 or example.com"
                />
              </label>
              <div className="admin-form-actions">
                <button type="button" className="admin-secondary-button" onClick={runPing}>
                  Run Ping
                </button>
                <button type="button" className="admin-danger-button" onClick={runReboot}>
                  Reboot Controller
                </button>
                <button type="button" className="admin-secondary-button" onClick={startCliSession}>
                  Start CLI Session
                </button>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Tool Result">
            {toolState.result ? <JsonBlock value={toolState.result} /> : <EmptyState title="Run a tool action to inspect the result." />}
          </SectionCard>
        </div>
      ) : null}
    </div>
  );
};

export const BusinessReportsPage = ({ history, onUnauthorized }) => {
  const [filters, setFilters] = useState(() => buildDefaultReportRange());
  const [draftFilters, setDraftFilters] = useState(() => buildDefaultReportRange());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [report, setReport] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await adminRequest(
          "get",
          "/v1/internal-admin/reports/business",
          null,
          onUnauthorized,
          {
            params: filters,
          },
        );

        if (!active) {
          return;
        }

        setReport(response.data.data);
      } catch (requestError) {
        if (!active) {
          return;
        }

        setError(extractMessage(requestError));
        setReport(null);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [filters, onUnauthorized]);

  const updateDraftFilter = (key, value) => {
    setDraftFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();
    setFilters({
      ...draftFilters,
      enterpriseId: draftFilters.enterpriseId?.trim() || "",
    });
    setNotice("");
  };

  const handleResetFilters = () => {
    const nextFilters = buildDefaultReportRange();
    setDraftFilters(nextFilters);
    setFilters(nextFilters);
    setNotice("");
  };

  const handleBackfill = async () => {
    setBackfilling(true);
    setError("");
    setNotice("");

    try {
      const response = await adminRequest(
        "post",
        "/v1/internal-admin/reports/business/backfill",
        { days: REPORT_RANGE_DEFAULT_DAYS },
        onUnauthorized,
      );
      setNotice(
        `Backfilled ${response.data.data.daysProcessed} days through ${response.data.data.lastSnapshotDate}.`,
      );
      setFilters((current) => ({ ...current }));
    } catch (requestError) {
      setError(extractMessage(requestError));
    } finally {
      setBackfilling(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError("");
    setNotice("");

    try {
      const response = await adminRequest(
        "get",
        "/v1/internal-admin/reports/business/export.csv",
        null,
        onUnauthorized,
        {
          params: filters,
          responseType: "blob",
        },
      );

      const fallbackFileName = `business-report-${filters.from}-to-${filters.to}.csv`;
      downloadBlob(
        response.data,
        extractDownloadFileName(
          response.headers?.["content-disposition"],
          fallbackFileName,
        ),
      );
      setNotice("CSV export generated.");
    } catch (requestError) {
      setError(extractMessage(requestError));
    } finally {
      setExporting(false);
    }
  };

  const handleEnterpriseDrillIn = (enterpriseId) => {
    const nextFilters = {
      ...draftFilters,
      enterpriseId: String(enterpriseId),
    };
    setDraftFilters(nextFilters);
    setFilters(nextFilters);
    setNotice("");
  };

  const current = report?.current || {};
  const windowTotals = report?.windowTotals || {};
  const trends = report?.trends || [];
  const snapshotStatus = report?.snapshotStatus || {};
  const latestTrendMetrics = report?.latestTrend?.metrics || {};
  const latestSnapshotDate = report?.latestTrend?.snapshotDate || "n/a";
  const activity = report?.reportActivity || {};

  if (loading) {
    return <LoadingView />;
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Business Reports</h1>
          <p>Business-facing footprint, operational leverage, and product adoption for the admin-enabled deployment.</p>
        </div>
        <div className="admin-card-actions">
          <button
            type="button"
            className="admin-secondary-button"
            onClick={() => setFilters((currentFilters) => ({ ...currentFilters }))}
            disabled={loading}
          >
            Refresh
          </button>
          <button
            type="button"
            className="admin-secondary-button"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <button
            type="button"
            className="admin-primary-button"
            onClick={handleBackfill}
            disabled={backfilling}
          >
            {backfilling ? "Backfilling…" : "Backfill Last 30 Days"}
          </button>
        </div>
      </div>

      <InlineMessage tone="error">{error}</InlineMessage>
      <InlineMessage tone="info">{notice}</InlineMessage>

      <SectionCard
        title="Filters"
        subtitle="Filters apply to both live footprint cards and snapshot-driven trend views."
      >
        <form className="admin-form" onSubmit={handleApplyFilters}>
          <div className="admin-form-grid">
            <label>
              <span>From</span>
              <input
                type="date"
                value={draftFilters.from}
                onChange={(event) => updateDraftFilter("from", event.target.value)}
              />
            </label>
            <label>
              <span>To</span>
              <input
                type="date"
                value={draftFilters.to}
                onChange={(event) => updateDraftFilter("to", event.target.value)}
              />
            </label>
            <label>
              <span>Primary Segment</span>
              <select
                value={draftFilters.primarySegment}
                onChange={(event) =>
                  updateDraftFilter("primarySegment", event.target.value)
                }
              >
                <option value="">All segments</option>
                {PRIMARY_CUSTOMER_SEGMENT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Use Case</span>
              <select
                value={draftFilters.useCaseTag}
                onChange={(event) =>
                  updateDraftFilter("useCaseTag", event.target.value)
                }
              >
                <option value="">All use cases</option>
                {USE_CASE_TAG_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Enterprise ID</span>
              <input
                type="text"
                value={draftFilters.enterpriseId}
                onChange={(event) =>
                  updateDraftFilter("enterpriseId", event.target.value)
                }
                placeholder="Optional account drilldown"
              />
            </label>
          </div>
          <div className="admin-form-actions">
            <button type="submit" className="admin-primary-button">
              Apply Filters
            </button>
            <button
              type="button"
              className="admin-secondary-button"
              onClick={handleResetFilters}
            >
              Reset
            </button>
          </div>
        </form>
      </SectionCard>

      <SectionCard
        title="Current Footprint"
        subtitle="Live exact state for the currently selected slice."
        actions={
          <TagPill tone="success">
            Exact live cards
          </TagPill>
        }
      >
        <div className="admin-stats-grid">
          <StatCard label="Enterprises" value={formatMetricValue(current.enterpriseCount)} />
          <StatCard label="Devices" value={formatMetricValue(current.deviceCount)} />
          <StatCard
            label="Online Devices"
            value={formatMetricValue(current.onlineDeviceCount)}
            tone="success"
          />
          <StatCard
            label="Offline Devices"
            value={formatMetricValue(current.offlineDeviceCount)}
            tone="warning"
          />
          <StatCard
            label="Active Alerts"
            value={formatMetricValue(current.activeAlertCount)}
            tone="critical"
          />
        </div>
      </SectionCard>

      <div className="admin-two-column">
        <SectionCard title="Operational Leverage" subtitle="ROI proxies tied to remote remediation and automation.">
          <div className="admin-stats-grid">
            <StatCard
              label="Outlet Actions"
              value={formatMetricValue(windowTotals.outlet_actions_day)}
            />
            <StatCard
              label="Controller Reboots"
              value={formatMetricValue(windowTotals.controller_reboots_day)}
            />
            <StatCard
              label="Alert Events"
              value={formatMetricValue(windowTotals.alert_events_day)}
            />
            <StatCard
              label="Automation Runs"
              value={formatMetricValue(windowTotals.automation_runs_day)}
            />
            <StatCard
              label="Automation Success Rate"
              value={formatPercent(
                windowTotals.automation_success_day,
                windowTotals.automation_runs_day,
              )}
              tone="success"
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Product Adoption"
          subtitle="Telemetry participation and the latest estimated installed-base mix."
        >
          <div className="admin-stats-grid">
            <StatCard
              label="Telemetry Active Device Days"
              value={formatMetricValue(windowTotals.telemetry_active_devices_day)}
            />
            <StatCard
              label="Latest Active Devices"
              value={formatMetricValue(latestTrendMetrics.estimated_active_devices_day)}
              tone="success"
            />
            <StatCard
              label="Latest Inactive Devices"
              value={formatMetricValue(latestTrendMetrics.estimated_inactive_devices_day)}
              tone="warning"
            />
            <StatCard
              label="Latest Device Footprint"
              value={formatMetricValue(latestTrendMetrics.estimated_device_footprint_day)}
            />
          </div>
          <div className="admin-report-meta">
            <span>
              Latest snapshot:{" "}
              <strong>{latestSnapshotDate}</strong>
            </span>
          </div>
        </SectionCard>
      </div>

      <div className="admin-two-column">
        <MultiSeriesTrendChart
          title="Footprint Trend"
          subtitle="Historical footprint is approximate by design and labeled accordingly."
          rows={trends}
          series={REPORT_FOOTPRINT_SERIES}
        />
        <MultiSeriesTrendChart
          title="Usage Trend"
          subtitle="Operational behavior across the selected report window."
          rows={trends}
          series={REPORT_USAGE_SERIES}
        />
      </div>

      <SectionCard
        title="Top Accounts"
        subtitle="Accounts ranked by operational activity and installed footprint."
      >
        {report?.topAccounts?.length ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Segment</th>
                <th>Use Cases</th>
                <th>Devices</th>
                <th>Online</th>
                <th>Alerts</th>
                <th>Outlet Actions</th>
                <th>Automation Runs</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {report.topAccounts.slice(0, 15).map((account) => (
                <tr key={account.enterpriseId}>
                  <td>{account.enterpriseName}</td>
                  <td>{formatSegmentLabel(account.primaryCustomerSegment)}</td>
                  <td>
                    {account.useCaseTags?.length ? (
                      <div className="admin-tag-list">
                        {account.useCaseTags.map((tag) => (
                          <TagPill key={`${account.enterpriseId}-${tag}`}>
                            {formatUseCaseLabel(tag)}
                          </TagPill>
                        ))}
                      </div>
                    ) : (
                      "Unassigned"
                    )}
                  </td>
                  <td>{formatMetricValue(account.current.deviceCount)}</td>
                  <td>{formatMetricValue(account.current.onlineDeviceCount)}</td>
                  <td>{formatMetricValue(account.current.activeAlertCount)}</td>
                  <td>{formatMetricValue(account.windowTotals.outlet_actions_day)}</td>
                  <td>{formatMetricValue(account.windowTotals.automation_runs_day)}</td>
                  <td>
                    <div className="admin-inline-actions">
                      <button
                        type="button"
                        className="admin-secondary-button"
                        onClick={() => handleEnterpriseDrillIn(account.enterpriseId)}
                      >
                        Drill In
                      </button>
                      <button
                        type="button"
                        className="admin-secondary-button"
                        onClick={() => history.push(`/admin/enterprises/${account.enterpriseId}`)}
                      >
                        Open
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState title="No enterprise rows matched the current filter." />
        )}
      </SectionCard>

      <div className="admin-two-column">
        <SectionCard title="Segment Breakdown">
          {report?.segmentBreakdown?.length ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Segment</th>
                  <th>Accounts</th>
                  <th>Devices</th>
                  <th>Outlet Actions</th>
                  <th>Automation Runs</th>
                </tr>
              </thead>
              <tbody>
                {report.segmentBreakdown.map((row) => (
                  <tr key={row.key}>
                    <td>{formatSegmentLabel(row.key)}</td>
                    <td>{formatMetricValue(row.enterpriseCount)}</td>
                    <td>{formatMetricValue(row.current.deviceCount)}</td>
                    <td>{formatMetricValue(row.windowTotals.outlet_actions_day)}</td>
                    <td>{formatMetricValue(row.windowTotals.automation_runs_day)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyState title="No segment data for this filter." />
          )}
        </SectionCard>

        <SectionCard title="Use Case Breakdown">
          {report?.useCaseBreakdown?.length ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Use Case</th>
                  <th>Accounts</th>
                  <th>Devices</th>
                  <th>Outlet Actions</th>
                  <th>Automation Runs</th>
                </tr>
              </thead>
              <tbody>
                {report.useCaseBreakdown.map((row) => (
                  <tr key={row.key}>
                    <td>{formatUseCaseLabel(row.key)}</td>
                    <td>{formatMetricValue(row.enterpriseCount)}</td>
                    <td>{formatMetricValue(row.current.deviceCount)}</td>
                    <td>{formatMetricValue(row.windowTotals.outlet_actions_day)}</td>
                    <td>{formatMetricValue(row.windowTotals.automation_runs_day)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyState title="No use-case data for this filter." />
          )}
        </SectionCard>
      </div>

      <div className="admin-two-column">
        <SectionCard title="Snapshot Status">
          <div className="admin-kv-grid">
            <div className="admin-kv">
              <span>Days Requested</span>
              <strong>{formatMetricValue(snapshotStatus.totalDaysRequested)}</strong>
            </div>
            <div className="admin-kv">
              <span>Days Available</span>
              <strong>{formatMetricValue(snapshotStatus.availableDays)}</strong>
            </div>
            <div className="admin-kv">
              <span>Latest Generated</span>
              <strong>{formatDateTime(snapshotStatus.latestGeneratedAt)}</strong>
            </div>
          </div>
          <div className="admin-report-meta">
            <span>Missing days:</span>
            <strong>
              {snapshotStatus.missingDays?.length
                ? snapshotStatus.missingDays
                    .slice(0, 6)
                    .map((day) => formatShortUtcDate(day))
                    .join(", ")
                : "None"}
              {snapshotStatus.missingDays?.length > 6
                ? ` +${snapshotStatus.missingDays.length - 6} more`
                : ""}
            </strong>
          </div>
        </SectionCard>

        <SectionCard title="Report Usage Trend" subtitle="Views, exports, backfills, and scheduled snapshot runs for this reporting surface.">
          {activity.daily?.length ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Views</th>
                  <th>Exports</th>
                  <th>Backfills</th>
                  <th>Snapshots</th>
                </tr>
              </thead>
              <tbody>
                {activity.daily.map((row) => (
                  <tr key={row.date}>
                    <td>{formatShortUtcDate(row.date)}</td>
                    <td>{formatMetricValue(row.viewCount)}</td>
                    <td>{formatMetricValue(row.exportCount)}</td>
                    <td>{formatMetricValue(row.backfillCount)}</td>
                    <td>{formatMetricValue(row.snapshotGenerateCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyState title="No report activity rows yet." />
          )}
        </SectionCard>
      </div>

      <SectionCard title="Recent Report Activity">
        {activity.recent?.length ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Status</th>
                <th>Target</th>
              </tr>
            </thead>
            <tbody>
              {activity.recent.map((row) => (
                <tr key={row.id}>
                  <td>{formatDateTime(row.created_at)}</td>
                  <td>{row.actor_email_id || "system"}</td>
                  <td>{row.action}</td>
                  <td>{row.status}</td>
                  <td>{row.target_type}:{row.target_id || "n/a"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState title="No recent report actions recorded." />
        )}
      </SectionCard>
    </div>
  );
};

const AuditPage = ({ onUnauthorized }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await adminRequest(
        "get",
        "/v1/internal-admin/audit",
        null,
        onUnauthorized,
        {
          params: {
            limit: 100,
          },
        },
      );
      setRows(response.data.data || []);
    } catch (error) {
      setError(extractMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, []);

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Audit</h1>
          <p>Deployment administrator write actions and support operations.</p>
        </div>
      </div>

      <InlineMessage tone="error">{error}</InlineMessage>

      <div className="admin-two-column">
        <SectionCard title="Audit Rows">
          {loading ? <LoadingView /> : null}
          {!loading && !rows.length ? <EmptyState title="No audit rows yet." /> : null}
          {!loading && rows.length ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Status</th>
                  <th>Target</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="clickable"
                    onClick={() => setSelected(row)}
                  >
                    <td>{formatDateTime(row.created_at)}</td>
                    <td>{row.actor_email_id || "n/a"}</td>
                    <td>{row.action}</td>
                    <td>{row.status}</td>
                    <td>
                      {row.target_type}:{row.target_id || "n/a"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </SectionCard>

        <SectionCard title="Audit Detail">
          {selected ? <JsonBlock value={selected} /> : <EmptyState title="Select an audit row." />}
        </SectionCard>
      </div>
    </div>
  );
};

const AdminShell = ({ adminUser, onLogout, onUnauthorized }) => {
  return (
    <AdminLayout adminUser={adminUser} onLogout={onLogout}>
      <Switch>
        <Route
          path="/admin/enterprises/new"
          exact
          render={(props) => (
            <EnterpriseCreatePage {...props} onUnauthorized={onUnauthorized} />
          )}
        />
        <Route
          path="/admin/enterprises/:enterpriseId"
          exact
          render={(props) => (
            <EnterpriseDetailPage {...props} onUnauthorized={onUnauthorized} />
          )}
        />
        <Route
          path="/admin/enterprises"
          exact
          render={(props) => (
            <EnterprisesListPage {...props} onUnauthorized={onUnauthorized} />
          )}
        />
        <Route
          path="/admin/users/:userId"
          exact
          render={(props) => (
            <AdminUserProfilePage {...props} onUnauthorized={onUnauthorized} />
          )}
        />
        <Route
          path="/admin/users"
          exact
          render={(props) => (
            <AdminUsersListPage {...props} onUnauthorized={onUnauthorized} />
          )}
        />
        <Route
          path="/admin/firmware-catalog/new"
          exact
          render={(props) => (
            <FirmwareCatalogEditorPage {...props} onUnauthorized={onUnauthorized} />
          )}
        />
        <Route
          path="/admin/firmware-catalog/:catalogId"
          exact
          render={(props) => (
            <FirmwareCatalogEditorPage {...props} onUnauthorized={onUnauthorized} />
          )}
        />
        <Route
          path="/admin/firmware-catalog"
          exact
          render={(props) => (
            <FirmwareCatalogListPage {...props} onUnauthorized={onUnauthorized} />
          )}
        />
        <Route
          path="/admin/devices/:serial"
          exact
          render={(props) => (
            <DeviceDebuggerPage {...props} onUnauthorized={onUnauthorized} />
          )}
        />
        <Route
          path="/admin/reports/business"
          exact
          render={(props) => (
            <BusinessReportsPage {...props} onUnauthorized={onUnauthorized} />
          )}
        />
        <Route
          path="/admin/audit"
          exact
          render={(props) => <AuditPage {...props} onUnauthorized={onUnauthorized} />}
        />
        <Route
          path="/admin"
          exact
          render={(props) => <DashboardPage {...props} onUnauthorized={onUnauthorized} />}
        />
        <Route
          render={(props) => (
            <NotFoundPage
              {...props}
              homePath="/admin"
              homeLabel="Go to admin dashboard"
            />
          )}
        />
      </Switch>
    </AdminLayout>
  );
};

export default function AdminRoot({ history }) {
  const [enabled, setEnabled] = useState(false);
  const [metaLoading, setMetaLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [adminUser, setAdminUser] = useState(null);
  const [loginError, setLoginError] = useState("");

  const loadMeta = async () => {
    setMetaLoading(true);
    try {
      const response = await axios.get("/v1/internal-admin/meta");
      setEnabled(Boolean(response.data?.data?.enabled));
      if (response.data?.data?.authenticated && response.data?.data?.adminUser) {
        setAdminUser(response.data.data.adminUser);
      }
    } catch (_error) {
      setEnabled(false);
    } finally {
      setMetaLoading(false);
    }
  };

  const loadMe = async () => {
    const token = getAdminToken();
    if (!token) {
      setAuthLoading(false);
      return;
    }

    try {
      const response = await adminRequest(
        "get",
        "/v1/internal-admin/auth/me",
        null,
        () => {
          setAdminUser(null);
          history.replace("/admin/login");
        },
      );
      setAdminUser(response.data.data);
    } catch (_error) {
      setAdminUser(null);
    } finally {
      setAuthLoading(false);
    }
  };

  // Initial admin session bootstrap runs once on shell mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadMeta();
    loadMe();
  }, []);

  const handleLogin = async ({ email, password, stayLoggedIn }) => {
    setLoginError("");
    try {
      const response = await axios.post("/v1/internal-admin/auth/login", {
        email,
        password,
        stayLoggedIn,
      });
      localStorage.setItem(ADMIN_TOKEN_KEY, response.data.token);
      localStorage.setItem(
        ADMIN_TOKEN_EXPIRATION_KEY,
        String(Date.now() + response.data.tokenExpirationTime * 1000),
      );
      setAdminUser(response.data.adminUser);
      history.replace("/admin");
    } catch (error) {
      setLoginError(extractMessage(error));
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      if (getAdminToken()) {
        await adminRequest(
          "post",
          "/v1/internal-admin/auth/logout",
          {},
          () => null,
        );
      }
    } catch (_error) {
      // best effort
    } finally {
      clearAdminSession();
      setAdminUser(null);
      history.replace("/admin/login");
    }
  };

  const handleUnauthorized = () => {
    clearAdminSession();
    setAdminUser(null);
    history.replace("/admin/login");
  };

  if (metaLoading || authLoading) {
    return (
      <div className="admin-root">
        <LoadingView />
      </div>
    );
  }

  return (
    <div className="admin-root">
      <Switch>
        <Route
          path="/admin/login"
          exact
          render={() =>
            adminUser ? (
              <Redirect to="/admin" />
            ) : (
              <AdminLoginPage
                enabled={enabled}
                loading={false}
                errorMessage={loginError}
                onLogin={handleLogin}
              />
            )
          }
        />
        <Route
          render={() =>
            adminUser ? (
              <AdminShell
                adminUser={adminUser}
                onLogout={handleLogout}
                onUnauthorized={handleUnauthorized}
              />
            ) : (
              <Redirect to="/admin/login" />
            )
          }
        />
      </Switch>
    </div>
  );
}
